import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { PHRASES } from './data/phrases';
import { FLASH_CARDS } from './data/flashCards';
import { COMPLETE_SENTENCES } from './data/completeSentences';
import { WORD_BUILDER } from './data/wordBuilder';
import { ODD_ONE_OUT } from './data/oddOneOut';
import { MODISMOS } from './data/modismos';
import { CONJUGATE_ITEMS } from './data/conjugateItems';
import { PREPOSITION_ITEMS } from './data/prepositionItems';
import { VERD_FALSO } from './data/verdFalso';
import { VOCAB_IN_CONTEXT } from './data/vocabInContext';
import { READING_ITEMS } from './data/readingItems';
import { LISTENING_ITEMS } from './data/listeningItems';
import { MISSIONS } from './data/missions';
import { GRAMMAR_TOPICS } from './data/grammarTopics';
import { MEDIA_DATA } from './data/mediaData';
import { WRITING_PROMPTS } from './data/writingPrompts';
import { SPEAKING_PROMPTS } from './data/speakingPrompts';
import { CURRICULUM } from './data/curriculum';
import { DICTATION_ITEMS } from './data/dictationItems';
import { TRANSLATION_ITEMS } from './data/translationItems';

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);

// ── HELPERS ───────────────────────────────────────────────────────────────────
function getDayOfYear(d = new Date()) {
  return Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);
}
function getWeekNumber(d = new Date()) {
  const start = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7);
}
function seededShuffle(arr, seed) {
  const a = [...arr]; let s = seed | 0;
  for (let i = a.length - 1; i > 0; i--) {
    s = (Math.imul(s ^ (s >>> 16), 0x45d9f3b) ^ Math.imul(s, 0x119de1f3));
    s = (s ^ (s >>> 16)) | 0;
    const j = Math.abs(s) % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function todayISO() { return new Date().toISOString().split('T')[0]; }
function getTodayPhrase() { return PHRASES[getDayOfYear() % PHRASES.length]; }
// Prioritizes items the student hasn't seen yet at this level, so replaying a game surfaces
// new content instead of repeating the same set. Once everything has been seen, it reviews
// already-seen items instead (shuffled fresh each time, not locked to the same 6 all day).
// Simplified Leitner-style spaced repetition. Each item has a "streak" of consecutive correct
// answers; a wrong answer resets it to 0. The longer the streak, the longer we wait before
// showing it again. Review intervals in days, indexed by streak (capped at the last value).
const REVIEW_INTERVALS_DAYS = [0, 1, 3, 7, 16, 35];

function computeItemSchedule(allCompletions) {
  const schedule = new Map(); // id -> { streak, lastDate }
  const sorted = [...allCompletions].sort((a, b) => (a.played_date || '').localeCompare(b.played_date || ''));
  for (const c of sorted) {
    (c.item_results || []).forEach(({ id, correct }) => {
      const prev = schedule.get(id) || { streak: 0, lastDate: null };
      schedule.set(id, { streak: correct ? prev.streak + 1 : 0, lastDate: c.played_date });
    });
  }
  return schedule;
}

function isDueForReview(schedule, id) {
  const entry = schedule.get(id);
  if (!entry || !entry.lastDate) return false;
  const interval = REVIEW_INTERVALS_DAYS[Math.min(entry.streak, REVIEW_INTERVALS_DAYS.length - 1)];
  const daysSince = Math.floor((Date.now() - new Date(entry.lastDate)) / 86400000);
  return daysSince >= interval;
}

// No artificial session cap: n defaults large enough to include every item in the pool
// at this level (due-for-review first, then unseen, then the rest). The student can keep
// going until the pool is exhausted, and can also stop whenever they want via the
// "Finish & see results" option that's always available once they've answered at least one.
function getSessionQuestions(type, level, previouslySeenIds, itemSchedule, sessionSeed = 0, n = 500) {
  const pool = (POOLS[type] || []).filter(item => item.level === level);
  const due = pool.filter(item => itemSchedule.has(item.id) && isDueForReview(itemSchedule, item.id));
  const unseen = pool.filter(item => !previouslySeenIds.has(item.id));
  const notDueYet = pool.filter(item => previouslySeenIds.has(item.id) && !due.includes(item));
  const randomSeed = Math.floor(Math.random() * 1_000_000) + sessionSeed;
  // Due-for-review items come first (spaced repetition), then unseen, then anything else as filler.
  const ordered = [
    ...seededShuffle(due, randomSeed),
    ...seededShuffle(unseen, randomSeed + 1),
    ...seededShuffle(notDueYet, randomSeed + 2),
  ];
  // De-duplicate in case an item appears in more than one bucket.
  const seenIds = new Set();
  const deduped = ordered.filter(item => (seenIds.has(item.id) ? false : (seenIds.add(item.id), true)));
  return deduped.slice(0, n);
}
function gamesAvailableAtLevel(level) {
  return GAME_TYPES.filter(type => POOLS[type].some(item => item.level === level));
}
function getCurrentMission() { return MISSIONS[getWeekNumber() % MISSIONS.length]; }

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const GAME_TYPES = ['flashcard', 'vocabulary', 'reading', 'listening', 'complete', 'wordbuilder', 'oddout', 'modismo', 'verdfalso', 'conjugate', 'preposition', 'writing', 'speaking', 'dictation', 'translation'];
const GAME_INFO = {
  flashcard:   { icon: '🃏', name: 'Flash Cards',        desc: { es: 'Vocabulario EN ↔ ES',           pt: 'Vocabulário EN ↔ ES',              en: 'Vocabulary EN ↔ ES' },           color: '#7c3aed', bg: 'linear-gradient(135deg,#7c3aed,#6d28d9)' },
  vocabulary:  { icon: '📗', name: 'Vocabulario en Contexto', desc: { es: 'Elige la palabra correcta',    pt: 'Escolha a palavra correta',         en: 'Choose the correct word' },       color: '#059669', bg: 'linear-gradient(135deg,#059669,#047857)' },
  reading:     { icon: '📰', name: 'Comprensión de Lectura', desc: { es: 'Lee y responde preguntas',     pt: 'Leia e responda perguntas',         en: 'Read and answer questions' },     color: '#2563eb', bg: 'linear-gradient(135deg,#2563eb,#1d4ed8)' },
  listening:   { icon: '🎧', name: 'Comprensión Auditiva', desc: { es: 'Escucha y responde preguntas', pt: 'Ouça e responda perguntas',        en: 'Listen and answer questions' },   color: '#0891b2', bg: 'linear-gradient(135deg,#0891b2,#0e7490)' },
  complete:    { icon: '✏️', name: 'Completa la Frase',  desc: { es: 'Elige la opción correcta',      pt: 'Escolha a opção correta',           en: 'Choose the correct option' },     color: '#0284c7', bg: 'linear-gradient(135deg,#0284c7,#0369a1)' },
  wordbuilder: { icon: '🔤', name: 'Arma la Frase',      desc: { es: 'Toca las palabras en orden',    pt: 'Toque nas palavras em ordem',       en: 'Tap the words in order' },        color: '#16a34a', bg: 'linear-gradient(135deg,#16a34a,#15803d)' },
  oddout:      { icon: '🔍', name: 'El Intruso',         desc: { es: '¿Cuál no pertenece al grupo?',  pt: 'Qual não pertence ao grupo?',       en: "Which one doesn't belong?" },     color: '#d97706', bg: 'linear-gradient(135deg,#d97706,#b45309)' },
  modismo:     { icon: '🔗', name: 'Modismos',           desc: { es: 'Elige el significado correcto', pt: 'Escolha o significado correto',     en: 'Choose the correct meaning' },    color: '#be185d', bg: 'linear-gradient(135deg,#be185d,#9d174d)' },
  verdfalso:   { icon: '✅', name: 'Verdadero o Falso',  desc: { es: 'Falsos amigos EN ↔ ES',          pt: 'Falsos cognatos EN ↔ ES',           en: 'False friends EN ↔ ES' },         color: '#0f766e', bg: 'linear-gradient(135deg,#0f766e,#0d9488)' },
  conjugate:   { icon: '🧩', name: 'Conjuga el Verbo',   desc: { es: 'Elige la forma correcta',       pt: 'Escolha a forma correta',           en: 'Choose the correct verb form' },  color: '#9333ea', bg: 'linear-gradient(135deg,#9333ea,#7e22ce)' },
  preposition: { icon: '🧷', name: 'Elige la Preposición', desc: { es: 'Por, para, de, en, a...',     pt: 'Por, para, de, en, a...',           en: 'Por, para, de, en, a...' },       color: '#ea580c', bg: 'linear-gradient(135deg,#ea580c,#c2410c)' },
  writing:     { icon: '📝', name: 'Escritura',          desc: { es: 'Escribe y compara con un modelo', pt: 'Escreva e compare com um modelo', en: 'Write and compare with a model' }, color: '#c026d3', bg: 'linear-gradient(135deg,#c026d3,#a21caf)' },
  speaking:    { icon: '🎙️', name: 'Expresión Oral',    desc: { es: 'Graba tu voz y compárala',      pt: 'Grave sua voz e compare',           en: 'Record your voice and compare' }, color: '#dc2626', bg: 'linear-gradient(135deg,#dc2626,#b91c1c)' },
  dictation:   { icon: '🎧✏️', name: 'Dictado',          desc: { es: 'Escucha y escribe lo que oyes', pt: 'Ouça e escreva o que ouve',         en: 'Listen and write what you hear' }, color: '#0d9488', bg: 'linear-gradient(135deg,#0d9488,#0f766e)' },
  translation: { icon: '🔁', name: 'Traducción',         desc: { es: 'Traduce al español',            pt: 'Traduza para o espanhol',           en: 'Translate into Spanish' },        color: '#7c2d12', bg: 'linear-gradient(135deg,#c2410c,#7c2d12)' },
};

// ── PHRASES (Spanish expressions with PT translations) ───────────────────────

// ── FLASH CARDS (Spanish vocab for PT speakers) ───────────────────────────────

// ── COMPLETE THE SENTENCE ─────────────────────────────────────────────────────

// ── WORD BUILDER ──────────────────────────────────────────────────────────────

// ── ODD ONE OUT ───────────────────────────────────────────────────────────────

// ── MODISMOS (Spanish idioms with PT meanings) ────────────────────────────────

// ── CONJUGATE THE VERB ────────────────────────────────────────────────────────

// ── CHOOSE THE PREPOSITION ────────────────────────────────────────────────────

// Single source of truth for type → question pool, used by getSessionQuestions, gamesAvailableAtLevel, and coverage calculations.

// ── VOCABULARY IN CONTEXT (choose the right word for the sentence) ───────────
// Level maps to the same beginner/intermediate/advanced tiers used everywhere else
// in Practice. Sentences and options stay in Spanish (immersion); only the
// explanatory tip is bilingual (tip = pt/es default, tip_en = English fallback),
// matching the convention already used by CompleteGame / ConjugateGame.

// ── READING COMPREHENSION (short passage + questions) ────────────────────────
// Each item pairs a short passage with ONE comprehension question, so a few
// items in the pool may share the same passage text with different questions —
// this gives real variety across sessions instead of a single fixed quiz.

// ── LISTENING COMPREHENSION (text-to-speech + comprehension question) ────────
// Uses the browser's built-in speech synthesis (Web Speech API) — no audio
// files, no hosting, no backend work needed. "text" is what gets read aloud;
// question/options are shown only after the student has listened at least once.

const POOLS = { flashcard: FLASH_CARDS, vocabulary: VOCAB_IN_CONTEXT, reading: READING_ITEMS, listening: LISTENING_ITEMS, complete: COMPLETE_SENTENCES, wordbuilder: WORD_BUILDER, oddout: ODD_ONE_OUT, modismo: MODISMOS, verdfalso: VERD_FALSO, conjugate: CONJUGATE_ITEMS, preposition: PREPOSITION_ITEMS, writing: WRITING_PROMPTS, speaking: SPEAKING_PROMPTS, dictation: DICTATION_ITEMS, translation: TRANSLATION_ITEMS };



// ── GRAMMAR SECTION CONTENT ───────────────────────────────────────────────────

function normalizeAnswer(str) {
  return (str || '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // strip accents so a missing accent doesn't wrongly fail someone
}

// ── GAME UI STRINGS ───────────────────────────────────────────────────────────
const GAME_STRINGS = {
  en: {
    practiceTitle: '🎮 Practice',
    practiceSubtitle: 'Practice whenever you want — new phrase and mission every day',
    game: 'Game', phrase: 'Phrase', mission: 'Mission',
    allGames: 'All games',
    coreExercisesLabel: 'Core exercises',
    coreExercisesSub: 'Vocabulary & reading, built for your level',
    productionLabel: 'Production skills',
    productionSub: 'Writing & speaking — build the skills MCQs can\'t',
    otherGamesLabel: 'Games',
    newBadge: 'New',
    done: '✓ Done', play: 'Play →',
    chooseOther: '← Choose another game',
    chooseLevelTitle: 'Choose your level',
    chooseLevelSub: 'Pick a level to see matching activities',
    levelBeginner: 'Beginner', levelIntermediate: 'Intermediate', levelAdvanced: 'Advanced',
    changeLevel: '← Change level',
    progressAtLevel: 'activities tried at this level',
    noContentLevel: 'No activities at this level yet — check back soon.',
    freeLabel: 'Free practice',
    questions: 'Questions', duration: 'Duration', total: 'Total played', newCardsLabel: 'New',
    playAgain: 'Play again →', startNow: 'Start now →',
    alreadyPlayed: 'You already played this today!', canPlayAgain: 'You can play again or choose another.',
    daysStreak: 'day streak', gamesTotal: 'games in total',
    phraseTitle: '💬 Expression of the day',
    phraseExample: 'Example:', phraseSub: 'New expression every day — 30 in total.',
    missionTitle: 'Weekly Mission',
    missionDesc: '🎯 Do this sometime this week — in a meeting, email, conversation or practising alone. The goal is to step out of your comfort zone in Spanish.',
    missionDone: 'Mission completed!', missionNext: 'New mission next week.',
    missionBtn: '✅ Mission completed!', saving: 'Saving...',
    saveResult: '💾 Save result', saved: 'Result saved! Come back tomorrow.',
    backMenu: '← Back to game menu',
    tryRemember: '💭 Try to remember the Spanish meaning before flipping the card.',
    tapReveal: 'Tap the card to reveal',
    tapSpanish: 'Tap to see in Spanish',
    wrong: '😅 Wrong', correct: '✅ Correct',
    feedbackCorrect: '✅ Correct!',
    feedbackWrong: '❌ Not quite',
    accuracyLabel: 'accuracy',
    correctAnswerLabel: 'Correct answer:',
    playAgainSession: '🔁 Practice again',
    keepGoing: '➡️ Keep going',
    finishSession: 'Finish & see results',
    allCaughtUp: "🎉 That's everything available at this level right now!",
    completeSentence: 'Complete the sentence:',
    conjugateLabel: 'Conjugate the verb:',
    prepositionLabel: 'Choose the preposition:',
    translateToSpanish: '🇧🇷 Translate to Spanish:',
    tapWords: 'Tap the words below...',
    verify: 'Check ✓',
    perfect: '🎉 Perfect!',
    wasCorrect: '❌ Correct answer was:',
    whichDoesntBelong: "Which one doesn't belong?",
    identify: 'Identify the odd one out',
    whatMeans: 'What does it mean?',
    fillBlank: 'Choose the word that fits:',
    readPassageLabel: 'Read the passage:',
    answerQuestionLabel: 'Answer the question:',
    continueToQuestion: 'Continue to the question →',
    listenLabel: '🔊 Listen',
    listenAgainLabel: '🔊 Listen again',
    listenInstructions: 'Tap to listen. You can replay it as many times as you like.',
    continueToQuestionListening: "I've listened — continue →",
    noSpeechSupport: 'Audio playback is not supported in this browser. Try Chrome or Edge.',
    writingPromptLabel: 'Writing prompt',
    writingPlaceholder: 'Write your answer in Spanish here...',
    wordCountLabel: 'words',
    minWordsHint: 'suggested minimum',
    checkMyWriting: 'Compare with a model answer',
    modelAnswerLabel: 'Model answer',
    keyPointsLabel: 'A strong answer would include:',
    selfRateLabel: 'How did you do?',
    rateNeedsPractice: '😅 Need more practice',
    rateGood: '👍 Pretty good',
    rateExcellent: '🌟 Excellent',
    speakingPromptLabel: 'Speaking prompt',
    noMicSupport: 'Microphone recording is not supported in this browser. Try Chrome or Edge.',
    micPermissionDenied: "Couldn't access the microphone. Check your browser's microphone permissions and try again.",
    startRecording: '🔴 Start recording',
    stopRecording: '⬛ Stop recording',
    recording: 'Recording…',
    playMyRecording: '▶️ Play my recording',
    playModelAnswer: '🔊 Listen to a model answer',
    recordAgain: 'Record again',
    seeModelResponse: 'Compare with a model response',
    dictationInstructions: 'Listen carefully and type exactly what you hear.',
    playDictation: '🔊 Play',
    playDictationAgain: '🔊 Play again',
    checkDictation: 'Check my answer',
    dictationOriginal: 'Original sentence',
    wordsCorrect: 'words correct',
    translationSourceLabel: 'Translate into Spanish',
    translationPlaceholder: 'Write the Spanish translation here...',
    checkTranslation: 'Compare with the model translation',
    rereadHint: 'Not quite — try rereading the passage carefully.',
    correct_count: 'correct',
    mediaTab: 'Media',
    grammarTab: 'Grammar',
    planTab: 'Plan',
    planChooseLevel: "What's your level?",
    planChooseLevelSub: 'Pick a level to see your 12-week study plan',
    planChangeLevel: '← Change level',
    planWeekLabel: 'Week',
    planTasksDone: 'tasks done',
    planItemsTarget: 'items',
    planStartTask: 'Start →',
    grammarChooseLevel: 'Choose your level',
    grammarTopics: 'Grammar Topics',
    grammarChangeLevel: '← Change level',
    grammarBackTopics: '← Back to topics',
    grammarCheck: 'Check',
    grammarNext: 'Next →',
    grammarBack: '← Back',
    grammarFinish: 'Finish',
    grammarReviewTitle: 'Review',
    grammarYourAnswer: 'Your answer:',
    grammarCorrectAnswer: 'Correct answer:',
    grammarDone: 'Done',
    mediaAll: 'All',
    mediaFilms: 'Films',
    mediaSeries: 'Series',
    mediaBooks: 'Books',
    mediaPodcasts: 'Podcasts',
    mediaBeginner: 'Beginner',
    mediaIntermediate: 'Intermediate',
    mediaBoth: 'Both levels',
    mediaWhere: 'Where to find:',
    mediaWhy: 'Why it helps:',
    verdTrue: '✅ True', verdFalse: '❌ False',
  },
  pt: {
    practiceTitle: '🎮 Praticar',
    practiceSubtitle: 'Pratique quando quiser — nova frase e missão todo dia',
    game: 'Jogo', phrase: 'Frase', mission: 'Missão',
    allGames: 'Todos os jogos',
    coreExercisesLabel: 'Exercícios essenciais',
    coreExercisesSub: 'Vocabulário e leitura, feitos para o seu nível',
    productionLabel: 'Habilidades de produção',
    productionSub: 'Escrita e fala — desenvolva o que os testes de múltipla escolha não conseguem',
    otherGamesLabel: 'Jogos',
    newBadge: 'Novo',
    done: '✓ Feito', play: 'Jogar →',
    chooseOther: '← Escolher outro jogo',
    chooseLevelTitle: 'Escolha seu nível',
    chooseLevelSub: 'Escolha um nível para ver as atividades correspondentes',
    levelBeginner: 'Iniciante', levelIntermediate: 'Intermediário', levelAdvanced: 'Avançado',
    changeLevel: '← Mudar de nível',
    progressAtLevel: 'atividades feitas neste nível',
    noContentLevel: 'Ainda não há atividades neste nível — volte em breve.',
    freeLabel: 'Prática livre',
    questions: 'Questões', duration: 'Duração', total: 'Total jogados', newCardsLabel: 'Novos',
    playAgain: 'Jogar novamente →', startNow: 'Começar agora →',
    alreadyPlayed: 'Você já jogou este hoje!', canPlayAgain: 'Pode jogar de novo ou escolher outro.',
    daysStreak: 'dia(s) de sequência', gamesTotal: 'jogos no total',
    phraseTitle: '💬 Expressão do dia',
    phraseExample: 'Exemplo de uso:', phraseSub: 'Nova expressão todo dia — 30 no total.',
    missionTitle: 'Missão da semana',
    missionDesc: '🎯 Faça isso em algum momento desta semana — em uma reunião, e-mail, conversa ou praticando sozinho. O objetivo é sair da zona de conforto no espanhol.',
    missionDone: 'Missão cumprida!', missionNext: 'Nova missão na próxima semana.',
    missionBtn: '✅ Missão cumprida!', saving: 'Salvando...',
    saveResult: '💾 Salvar resultado', saved: 'Resultado salvo! Volte amanhã.',
    backMenu: '← Voltar ao menu de jogos',
    tryRemember: '💭 Tente lembrar o significado em espanhol antes de virar o card.',
    tapReveal: 'Toque no card para revelar',
    tapSpanish: 'Toque para ver em espanhol',
    wrong: '😅 Errei', correct: '✅ Acertei',
    feedbackCorrect: '✅ Correto!',
    feedbackWrong: '❌ Quase!',
    accuracyLabel: 'de acerto',
    correctAnswerLabel: 'Resposta correta:',
    playAgainSession: '🔁 Praticar de novo',
    keepGoing: '➡️ Continuar',
    finishSession: 'Terminar e ver resultado',
    allCaughtUp: '🎉 Isso é tudo que temos neste nível por agora!',
    completeSentence: 'Complete a frase:',
    conjugateLabel: 'Conjugue o verbo:',
    prepositionLabel: 'Escolha a preposição:',
    translateToSpanish: '🇧🇷 Traduza para o espanhol:',
    tapWords: 'Toque nas palavras abaixo...',
    verify: 'Verificar ✓',
    perfect: '🎉 Perfeito!',
    wasCorrect: '❌ Era:',
    whichDoesntBelong: 'Qual não pertence ao grupo?',
    identify: 'Identifique o intruso',
    whatMeans: 'O que significa?',
    fillBlank: 'Escolha a palavra correta:',
    readPassageLabel: 'Leia o texto:',
    answerQuestionLabel: 'Responda à pergunta:',
    continueToQuestion: 'Continuar para a pergunta →',
    listenLabel: '🔊 Ouvir',
    listenAgainLabel: '🔊 Ouvir de novo',
    listenInstructions: 'Toque para ouvir. Você pode repetir quantas vezes quiser.',
    continueToQuestionListening: 'Já ouvi — continuar →',
    noSpeechSupport: 'A reprodução de áudio não é compatível com este navegador. Tente o Chrome ou o Edge.',
    writingPromptLabel: 'Tema de escrita',
    writingPlaceholder: 'Escreva sua resposta em espanhol aqui...',
    wordCountLabel: 'palavras',
    minWordsHint: 'mínimo sugerido',
    checkMyWriting: 'Comparar com uma resposta modelo',
    modelAnswerLabel: 'Resposta modelo',
    keyPointsLabel: 'Uma boa resposta deve incluir:',
    selfRateLabel: 'Como você se saiu?',
    rateNeedsPractice: '😅 Preciso praticar mais',
    rateGood: '👍 Muito bem',
    rateExcellent: '🌟 Excelente',
    speakingPromptLabel: 'Tema de expressão oral',
    noMicSupport: 'A gravação por microfone não é compatível com este navegador. Tente o Chrome ou o Edge.',
    micPermissionDenied: 'Não foi possível acessar o microfone. Verifique as permissões de microfone do navegador e tente novamente.',
    startRecording: '🔴 Começar a gravar',
    stopRecording: '⬛ Parar de gravar',
    recording: 'Gravando…',
    playMyRecording: '▶️ Ouvir minha gravação',
    playModelAnswer: '🔊 Ouvir uma resposta modelo',
    recordAgain: 'Gravar de novo',
    seeModelResponse: 'Comparar com uma resposta modelo',
    dictationInstructions: 'Ouça com atenção e digite exatamente o que você ouve.',
    playDictation: '🔊 Ouvir',
    playDictationAgain: '🔊 Ouvir de novo',
    checkDictation: 'Verificar minha resposta',
    dictationOriginal: 'Frase original',
    wordsCorrect: 'palavras corretas',
    translationSourceLabel: 'Traduza para o espanhol',
    translationPlaceholder: 'Escreva a tradução em espanhol aqui...',
    checkTranslation: 'Comparar com a tradução modelo',
    rereadHint: 'Quase — tente reler o texto com atenção.',
    correct_count: 'certas',
    mediaTab: 'Mídia',
    grammarTab: 'Gramática',
    planTab: 'Plano',
    planChooseLevel: 'Qual é o seu nível?',
    planChooseLevelSub: 'Escolha um nível para ver seu plano de estudos de 12 semanas',
    planChangeLevel: '← Mudar de nível',
    planWeekLabel: 'Semana',
    planTasksDone: 'tarefas concluídas',
    planItemsTarget: 'itens',
    planStartTask: 'Começar →',
    grammarChooseLevel: 'Escolha seu nível',
    grammarTopics: 'Tópicos de Gramática',
    grammarChangeLevel: '← Mudar de nível',
    grammarBackTopics: '← Voltar aos tópicos',
    grammarCheck: 'Verificar',
    grammarNext: 'Próxima →',
    grammarBack: '← Voltar',
    grammarFinish: 'Finalizar',
    grammarReviewTitle: 'Revisão',
    grammarYourAnswer: 'Sua resposta:',
    grammarCorrectAnswer: 'Resposta correta:',
    grammarDone: 'Concluído',
    mediaAll: 'Todos',
    mediaFilms: 'Filmes',
    mediaSeries: 'Séries',
    mediaBooks: 'Livros',
    mediaPodcasts: 'Podcasts',
    mediaBeginner: 'Iniciante',
    mediaIntermediate: 'Intermediário',
    mediaBoth: 'Todos os níveis',
    mediaWhere: 'Onde encontrar:',
    mediaWhy: 'Por que ajuda:',
    verdTrue: '✅ Verdadeiro', verdFalse: '❌ Falso',
  },
  es: {
    practiceTitle: '🎮 Practicar',
    practiceSubtitle: 'Practica cuando quieras — nueva frase y misión cada día',
    game: 'Juego', phrase: 'Frase', mission: 'Misión',
    allGames: 'Todos los juegos',
    coreExercisesLabel: 'Ejercicios fundamentales',
    coreExercisesSub: 'Vocabulario y lectura, hechos para tu nivel',
    productionLabel: 'Habilidades de producción',
    productionSub: 'Escritura y expresión oral — desarrollan lo que un test de opción múltiple no puede',
    otherGamesLabel: 'Juegos',
    newBadge: 'Nuevo',
    done: '✓ Hecho', play: 'Jugar →',
    chooseOther: '← Elegir otro juego',
    chooseLevelTitle: 'Elige tu nivel',
    chooseLevelSub: 'Elige un nivel para ver las actividades correspondientes',
    levelBeginner: 'Principiante', levelIntermediate: 'Intermedio', levelAdvanced: 'Avanzado',
    changeLevel: '← Cambiar de nivel',
    progressAtLevel: 'actividades hechas en este nivel',
    noContentLevel: 'Todavía no hay actividades en este nivel — vuelve pronto.',
    freeLabel: 'Práctica libre',
    questions: 'Preguntas', duration: 'Duración', total: 'Total jugados', newCardsLabel: 'Nuevas',
    playAgain: 'Jugar de nuevo →', startNow: 'Empezar ahora →',
    alreadyPlayed: '¡Ya jugaste este hoy!', canPlayAgain: 'Puedes jugar de nuevo o elegir otro.',
    daysStreak: 'día(s) seguidos', gamesTotal: 'juegos en total',
    phraseTitle: '💬 Expresión del día',
    phraseExample: 'Ejemplo de uso:', phraseSub: 'Nueva expresión cada día — 30 en total.',
    missionTitle: 'Misión de la semana',
    missionDesc: '🎯 Haz esto en algún momento de esta semana — en una reunión, correo, conversación o practicando solo. El objetivo es salir de la zona de conforto en español.',
    missionDone: '¡Misión cumplida!', missionNext: 'Nueva misión la próxima semana.',
    missionBtn: '✅ ¡Misión cumplida!', saving: 'Guardando...',
    saveResult: '💾 Guardar resultado', saved: '¡Resultado guardado! Vuelve mañana.',
    backMenu: '← Volver al menú',
    tryRemember: '💭 Intenta recordar el significado en español antes de girar la tarjeta.',
    tapReveal: 'Toca la tarjeta para revelar',
    tapSpanish: 'Toca para ver en español',
    wrong: '😅 Fallé', correct: '✅ Acerté',
    feedbackCorrect: '✅ ¡Correcto!',
    feedbackWrong: '❌ Casi',
    accuracyLabel: 'de aciertos',
    correctAnswerLabel: 'Respuesta correcta:',
    playAgainSession: '🔁 Practicar de nuevo',
    keepGoing: '➡️ Seguir',
    finishSession: 'Terminar y ver resultado',
    allCaughtUp: '🎉 ¡Eso es todo lo disponible en este nivel por ahora!',
    completeSentence: 'Completa la frase:',
    conjugateLabel: 'Conjuga el verbo:',
    prepositionLabel: 'Elige la preposición:',
    translateToSpanish: '🇧🇷 Traduce al español:',
    tapWords: 'Toca las palabras de abajo...',
    verify: 'Verificar ✓',
    perfect: '🎉 ¡Perfecto!',
    wasCorrect: '❌ Era:',
    whichDoesntBelong: '¿Cuál no pertenece al grupo?',
    identify: 'Identifica el intruso',
    whatMeans: '¿Qué significa?',
    fillBlank: 'Elige la palabra correcta:',
    readPassageLabel: 'Lee el texto:',
    answerQuestionLabel: 'Responde la pregunta:',
    continueToQuestion: 'Continuar a la pregunta →',
    listenLabel: '🔊 Escuchar',
    listenAgainLabel: '🔊 Escuchar de nuevo',
    listenInstructions: 'Toca para escuchar. Puedes repetirlo las veces que quieras.',
    continueToQuestionListening: 'Ya escuché — continuar →',
    noSpeechSupport: 'La reproducción de audio no es compatible con este navegador. Prueba con Chrome o Edge.',
    writingPromptLabel: 'Tema de escritura',
    writingPlaceholder: 'Escribe tu respuesta en español aquí...',
    wordCountLabel: 'palabras',
    minWordsHint: 'mínimo sugerido',
    checkMyWriting: 'Comparar con una respuesta modelo',
    modelAnswerLabel: 'Respuesta modelo',
    keyPointsLabel: 'Una buena respuesta debería incluir:',
    selfRateLabel: '¿Cómo te fue?',
    rateNeedsPractice: '😅 Necesito practicar más',
    rateGood: '👍 Bastante bien',
    rateExcellent: '🌟 ¡Excelente!',
    speakingPromptLabel: 'Tema de expresión oral',
    noMicSupport: 'La grabación con micrófono no es compatible con este navegador. Prueba con Chrome o Edge.',
    micPermissionDenied: 'No se pudo acceder al micrófono. Revisa los permisos de micrófono de tu navegador e inténtalo de nuevo.',
    startRecording: '🔴 Empezar a grabar',
    stopRecording: '⬛ Detener grabación',
    recording: 'Grabando…',
    playMyRecording: '▶️ Escuchar mi grabación',
    playModelAnswer: '🔊 Escuchar una respuesta modelo',
    recordAgain: 'Grabar de nuevo',
    seeModelResponse: 'Comparar con una respuesta modelo',
    dictationInstructions: 'Escucha con atención y escribe exactamente lo que oyes.',
    playDictation: '🔊 Escuchar',
    playDictationAgain: '🔊 Escuchar de nuevo',
    checkDictation: 'Revisar mi respuesta',
    dictationOriginal: 'Frase original',
    wordsCorrect: 'palabras correctas',
    translationSourceLabel: 'Traduce al español',
    translationPlaceholder: 'Escribe la traducción en español aquí...',
    checkTranslation: 'Comparar con la traducción modelo',
    rereadHint: 'Casi — intenta releer el texto con atención.',
    correct_count: 'correctas',
    mediaTab: 'Medios',
    grammarTab: 'Gramática',
    planTab: 'Plan',
    planChooseLevel: '¿Cuál es tu nivel?',
    planChooseLevelSub: 'Elige un nivel para ver tu plan de estudio de 12 semanas',
    planChangeLevel: '← Cambiar de nivel',
    planWeekLabel: 'Semana',
    planTasksDone: 'tareas completadas',
    planItemsTarget: 'elementos',
    planStartTask: 'Empezar →',
    grammarChooseLevel: 'Elige tu nivel',
    grammarTopics: 'Temas de Gramática',
    grammarChangeLevel: '← Cambiar de nivel',
    grammarBackTopics: '← Volver a los temas',
    grammarCheck: 'Comprobar',
    grammarNext: 'Siguiente →',
    grammarBack: '← Atrás',
    grammarFinish: 'Finalizar',
    grammarReviewTitle: 'Revisión',
    grammarYourAnswer: 'Tu respuesta:',
    grammarCorrectAnswer: 'Respuesta correcta:',
    grammarDone: 'Completado',
    mediaAll: 'Todo',
    mediaFilms: 'Películas',
    mediaSeries: 'Series',
    mediaBooks: 'Libros',
    mediaPodcasts: 'Podcasts',
    mediaBeginner: 'Principiante',
    mediaIntermediate: 'Intermedio',
    mediaBoth: 'Todos los niveles',
    mediaWhere: 'Dónde encontrar:',
    mediaWhy: 'Por qué ayuda:',
    verdTrue: '✅ Verdadero', verdFalse: '❌ Falso',
  },
};


// ── VERDADERO O FALSO ─────────────────────────────────────────────────────────
// Each entry: stmt (ES/PT comparison), stmt_en (ES/EN comparison), exp (ES explanation), exp_en (EN explanation)

// ── GAME COMPONENTS (same logic as Practice.js, Spanish labels) ───────────────
// Shared, unmissable feedback banner shown the instant a question is answered.
// Bigger and more explicit than color-only cues so it's obvious what happened
// even to someone clicking through quickly.
// Shuffles a multiple-choice question's options and remaps the answer index,
// so the correct answer isn't always in the same position. Used by every
// fixed-order MCQ game (Complete, Conjugate, Preposition, Vocabulary, Reading,
// Listening) — Modismo/OddOut already shuffle their own way, VerdFalso and
// WordBuilder aren't index-based so they don't need this.
// Compares a student's typed answer to an original sentence word-by-word (used
// by Dictation). Punctuation/case are normalized away since we're testing
// listening + spelling, not punctuation; accents and letters are NOT stripped,
// since those are exactly what dictation should catch.
function normalizeWordForCompare(w) {
  return w.toLowerCase().replace(/^[¿¡"'(]+|[.,;:!?"')]+$/g, '');
}
function compareDictation(original, typed) {
  const origWords = original.trim().split(/\s+/);
  const typedWords = (typed || '').trim().split(/\s+/).filter(Boolean);
  const results = origWords.map((w, i) => ({
    word: w,
    correct: typedWords[i] !== undefined && normalizeWordForCompare(typedWords[i]) === normalizeWordForCompare(w),
  }));
  const correctCount = results.filter(r => r.correct).length;
  return { results, correctCount, total: origWords.length, ratio: origWords.length ? correctCount / origWords.length : 0 };
}

function shuffleMC(q, seed) {
  if (!q || !Array.isArray(q.options)) return { options: [], order: [], answer: -1 };
  const order = seededShuffle(q.options.map((_, i) => i), seed);
  return { options: order.map(i => q.options[i]), answer: order.indexOf(q.answer), order };
}

function FeedbackBanner({ correct, gs }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', borderRadius: '14px', marginBottom: '14px',
      background: correct ? '#f0fdf4' : '#fff1f2', border: `2px solid ${correct ? '#86efac' : '#fecdd3'}`,
      animation: 'feedbackPop 0.25s ease-out',
    }}>
      <span style={{ fontSize: '1.6rem', flexShrink: 0 }}>{correct ? '✅' : '❌'}</span>
      <span style={{ fontWeight: '900', fontSize: '1.05rem', color: correct ? '#15803d' : '#be123c' }}>
        {correct ? gs.feedbackCorrect : gs.feedbackWrong}
      </span>
    </div>
  );
}

// Shared footer nav: Back / Next (or Finish, once the pool is exhausted) plus a
// subtle "finish & see results" link that's available the moment the student has
// answered at least one question — so nobody is ever forced to keep going, and
// nobody is ever cut off at an arbitrary count either.
function SessionFooter({ idx, isLast, answered, color, gs, onBack, onNext, onFinish }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: '10px' }}>
        {idx > 0 && (
          <button onClick={onBack} style={{ flex: 1, padding: '14px', background: 'white', border: '1.5px solid #e2e8f0', borderRadius: '14px', fontWeight: '800', fontSize: '0.85rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.grammarBack}</button>
        )}
        {!isLast ? (
          <button onClick={onNext} disabled={!answered} style={{ flex: 2, padding: '14px', background: answered ? color : '#f1f5f9', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.85rem', color: answered ? 'white' : '#94a3b8', cursor: answered ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.grammarNext}</button>
        ) : (
          <button onClick={onFinish} disabled={!answered} style={{ flex: 2, padding: '14px', background: answered ? '#16a34a' : '#f1f5f9', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.85rem', color: answered ? 'white' : '#94a3b8', cursor: answered ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.grammarFinish}</button>
        )}
      </div>
      {!isLast && answered && (
        <button onClick={onFinish} style={{ width: '100%', padding: '10px', marginTop: '8px', background: 'transparent', border: 'none', color: '#94a3b8', fontWeight: '700', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', textDecoration: 'underline' }}>{gs.finishSession}</button>
      )}
      {isLast && <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.76rem', fontWeight: '600', color: '#94a3b8' }}>{gs.allCaughtUp}</div>}
    </div>
  );
}

function FlashCardGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null)); // null | true | false

  const mark = (knew) => {
    const next = [...answers];
    next[idx] = knew;
    setAnswers(next);
  };

  const goNext = () => { setFlipped(false); setIdx(i => Math.min(i + 1, questions.length - 1)); };
  const goBack = () => { setFlipped(false); setIdx(i => Math.max(i - 1, 0)); };
  const finish = () => {
    const answeredIdxs = answers.map((a, i) => i).filter(i => answers[i] !== null);
    const score = answeredIdxs.filter(i => answers[i] === true).length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: answers[i] === true })));
  };

  const card = questions[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = card.topic || card.module;
  const isLast = idx === questions.length - 1;
  const answered = answers[idx] !== null;

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ marginBottom: '10px', fontSize: '0.75rem', fontWeight: '700', color: '#7c3aed' }}>{idx + 1} / {questions.length}</div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📘 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#ede9fe', borderRadius: '99px', marginBottom: '16px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#7c3aed', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ background: '#f5f3ff', border: '1.5px solid #ddd6fe', borderRadius: '12px', padding: '10px 16px', marginBottom: '20px', fontSize: '0.82rem', fontWeight: '700', color: '#6d28d9' }}>
        {gs.tryRemember}
      </div>
      <div onClick={() => setFlipped(f => !f)} style={{ background: flipped ? '#7c3aed' : 'white', border: '2px solid #ede9fe', borderRadius: '24px', padding: '48px 28px', marginBottom: '24px', cursor: 'pointer', transition: 'all 0.25s', boxShadow: '0 8px 32px rgba(124,58,237,0.12)', minHeight: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', userSelect: 'none' }}>
        {!flipped ? (
          <>
            <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '2px' }}>{gs.lang === 'pt' ? '🇧🇷 Português' : '🇬🇧 English'}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: '900', color: '#1e1b4b' }}>{gs.lang === 'pt' ? card.back : (card.back_en || card.back)}</div>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: '600', marginTop: '8px' }}>{gs.tapSpanish}</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: '0.7rem', fontWeight: '800', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '2px' }}>🇪🇸 Español</div>
            <div style={{ fontSize: '2rem', fontWeight: '900', color: 'white' }}>{card.front}</div>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#c4b5fd', marginTop: '4px', textAlign: 'center' }}>{gs.lang === 'pt' ? card.back : (card.back_en || card.back)}</div>
            <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)', marginTop: '10px', textAlign: 'center', fontWeight: '600', lineHeight: 1.4 }}>{gs.lang === 'pt' ? card.pt : (card.en || card.pt)}</div>
          </>
        )}
      </div>
      {flipped && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <button onClick={() => mark(false)} style={{ padding: '18px', background: answers[idx] === false ? '#fecdd3' : '#fff1f2', border: '2px solid #fecdd3', borderRadius: '16px', fontWeight: '800', fontSize: '0.95rem', color: '#be123c', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.wrong}</button>
          <button onClick={() => mark(true)} style={{ padding: '18px', background: answers[idx] === true ? '#bbf7d0' : '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: '16px', fontWeight: '800', fontSize: '0.95rem', color: '#15803d', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.correct}</button>
        </div>
      )}
      {!flipped && <div style={{ fontSize: '0.78rem', color: '#a78bfa', fontWeight: '600', marginBottom: '16px' }}>{gs.tapReveal}</div>}
      <SessionFooter idx={idx} isLast={isLast} answered={answered} color="#7c3aed" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

function CompleteGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null));

  const pick = (optIdx) => {
    const next = [...answers];
    next[idx] = optIdx;
    setAnswers(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = answers.map((a, i) => i).filter(i => answers[i] !== null);
    const score = answeredIdxs.filter(i => answers[i] === questions[i].answer).length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: answers[i] === questions[i].answer })));
  };

  const q = questions[idx];
  const mc = useMemo(() => shuffleMC(q, idx), [q, idx]);

  // Defensive fallback: if idx ever ends up past the end of the session, bail out
  // gracefully instead of throwing mid-render and blanking the whole screen.
  if (!q) return null;
  const selected = answers[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.topic || q.module;
  const isLast = idx === questions.length - 1;

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#0284c7', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📘 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#e0f2fe', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#0284c7', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ background: '#f0f9ff', border: '2px solid #0284c730', borderRadius: '20px', padding: '24px', marginBottom: '20px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#0284c7', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>{gs.completeSentence}</div>
        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.6 }}>
          {q.sentence.split('_____').map((part, i, arr) => (
            <span key={i}>{part}{i < arr.length - 1 && <span style={{ background: selected !== null ? (selected === q.answer ? '#bbf7d0' : '#fecdd3') : '#e0f2fe', borderBottom: '2px solid #0284c7', padding: '0 12px', borderRadius: '6px', fontWeight: '900', color: selected !== null ? (selected === q.answer ? '#15803d' : '#be123c') : '#0284c7' }}>{selected !== null ? q.options[q.answer] : '  ?  '}</span>}</span>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        {mc.options.map((opt, i) => {
          const originalIdx = mc.order[i];
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (selected !== null) {
            if (originalIdx === q.answer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            else if (originalIdx === selected) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
          }
          return <button key={i} onClick={() => pick(originalIdx)} style={{ padding: '16px 12px', background: bg, border, borderRadius: '14px', fontWeight: '700', fontSize: '0.9rem', color, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s', textAlign: 'center' }}>{opt}</button>;
        })}
      </div>
      {selected !== null && <FeedbackBanner correct={selected === q.answer} gs={gs} />}
      {selected !== null && <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>💡 {gs.lang === 'en' ? (q.tip_en || q.tip) : q.tip}</div>}
      <SessionFooter idx={idx} isLast={isLast} answered={selected !== null} color="#0284c7" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

function VocabInContextGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null));

  const pick = (optIdx) => {
    const next = [...answers];
    next[idx] = optIdx;
    setAnswers(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = answers.map((a, i) => i).filter(i => answers[i] !== null);
    const score = answeredIdxs.filter(i => answers[i] === questions[i].answer).length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: answers[i] === questions[i].answer })));
  };

  const q = questions[idx];
  const mc = useMemo(() => shuffleMC(q, idx), [q, idx]);

  // Defensive fallback: if idx ever ends up past the end of the session, bail out
  // gracefully instead of throwing mid-render and blanking the whole screen.
  if (!q) return null;
  const selected = answers[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.module;
  const isLast = idx === questions.length - 1;

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#059669', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📗 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#d1fae5', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#059669', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ background: '#ecfdf5', border: '2px solid #05966930', borderRadius: '20px', padding: '24px', marginBottom: '20px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#059669', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>{gs.fillBlank}</div>
        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.6 }}>
          {q.sentence.split('_____').map((part, i, arr) => (
            <span key={i}>{part}{i < arr.length - 1 && <span style={{ background: selected !== null ? (selected === q.answer ? '#bbf7d0' : '#fecdd3') : '#d1fae5', borderBottom: '2px solid #059669', padding: '0 12px', borderRadius: '6px', fontWeight: '900', color: selected !== null ? (selected === q.answer ? '#15803d' : '#be123c') : '#059669' }}>{selected !== null ? q.options[q.answer] : '  ?  '}</span>}</span>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        {mc.options.map((opt, i) => {
          const originalIdx = mc.order[i];
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (selected !== null) {
            if (originalIdx === q.answer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            else if (originalIdx === selected) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
          }
          return <button key={i} onClick={() => pick(originalIdx)} style={{ padding: '16px 12px', background: bg, border, borderRadius: '14px', fontWeight: '700', fontSize: '0.9rem', color, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s', textAlign: 'center' }}>{opt}</button>;
        })}
      </div>
      {selected !== null && <FeedbackBanner correct={selected === q.answer} gs={gs} />}
      {selected !== null && <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>💡 {gs.lang === 'en' ? (q.tip_en || q.tip) : q.tip}</div>}
      <SessionFooter idx={idx} isLast={isLast} answered={selected !== null} color="#059669" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

function ReadingGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null));
  const [stage, setStage] = useState('passage'); // 'passage' | 'question' — read first, then answer without the text in view

  useEffect(() => { setStage('passage'); }, [idx]);

  const pick = (optIdx) => {
    const next = [...answers];
    next[idx] = optIdx;
    setAnswers(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = answers.map((a, i) => i).filter(i => answers[i] !== null);
    const score = answeredIdxs.filter(i => answers[i] === questions[i].answer).length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: answers[i] === questions[i].answer })));
  };

  const q = questions[idx];
  const mc = useMemo(() => shuffleMC(q, idx), [q, idx]);

  // Defensive fallback: if idx ever ends up past the end of the session, bail out
  // gracefully instead of throwing mid-render and blanking the whole screen.
  if (!q) return null;
  const selected = answers[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.module;
  const isLast = idx === questions.length - 1;
  const wasCorrect = selected !== null && selected === q.answer;
  const wasWrong = selected !== null && selected !== q.answer;

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#2563eb', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📰 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#dbeafe', borderRadius: '99px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#2563eb', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      {stage === 'passage' ? (
        <>
          <div style={{ background: '#eff6ff', border: '2px solid #2563eb30', borderRadius: '20px', padding: '24px', marginBottom: '20px' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>{gs.readPassageLabel}</div>
            <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0f172a', lineHeight: 1.8 }}>{q.passage}</div>
          </div>
          <button onClick={() => setStage('question')} style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}>
            {gs.continueToQuestion}
          </button>
        </>
      ) : (
        <>
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '18px 20px', marginBottom: '16px' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.answerQuestionLabel}</div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', lineHeight: 1.5 }}>{q.question}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
            {mc.options.map((opt, i) => {
              const originalIdx = mc.order[i];
              let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
              if (originalIdx === selected) {
                if (wasCorrect) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
                else { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
              }
              return <button key={i} onClick={() => pick(originalIdx)} disabled={selected !== null} style={{ padding: '15px 18px', background: bg, border, borderRadius: '14px', fontWeight: '700', fontSize: '0.88rem', color, cursor: selected !== null ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s', textAlign: 'left' }}>
                <span style={{ fontWeight: '800', color: '#94a3b8', marginRight: '8px' }}>{String.fromCharCode(65 + i)}.</span>{opt}
              </button>;
            })}
          </div>
          {selected !== null && <FeedbackBanner correct={wasCorrect} gs={gs} />}
          {wasCorrect && <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>💡 {gs.lang === 'en' ? (q.tip_en || q.tip) : q.tip}</div>}
          {wasWrong && <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#9a3412', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>🔎 {gs.rereadHint}</div>}
        </>
      )}

      {stage === 'question' && <SessionFooter idx={idx} isLast={isLast} answered={selected !== null} color="#2563eb" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />}
    </div>
  );
}

function ListeningGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null));
  const [stage, setStage] = useState('listen'); // 'listen' | 'question'
  const [hasPlayed, setHasPlayed] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const speechSupported = typeof window !== 'undefined' && !!window.speechSynthesis;
  const isMounted = useRef(true);

  useEffect(() => { setStage('listen'); setHasPlayed(false); }, [idx]);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (speechSupported) window.speechSynthesis.cancel();
    };
  }, [speechSupported]);

  const play = () => {
    if (!speechSupported) return;
    const q = questions[idx];
    if (!q) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(q.text);
    utterance.lang = 'es-ES';
    utterance.rate = (q.level === 'A1' || q.level === 'A2') ? 0.85 : 0.95;
    utterance.onstart = () => { if (isMounted.current) setSpeaking(true); };
    utterance.onend = () => { if (isMounted.current) setSpeaking(false); };
    utterance.onerror = () => { if (isMounted.current) setSpeaking(false); };
    window.speechSynthesis.speak(utterance);
    setHasPlayed(true);
  };

  const pick = (optIdx) => {
    const next = [...answers];
    next[idx] = optIdx;
    setAnswers(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = answers.map((a, i) => i).filter(i => answers[i] !== null);
    const score = answeredIdxs.filter(i => answers[i] === questions[i].answer).length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: answers[i] === questions[i].answer })));
  };

  const q = questions[idx];
  const mc = useMemo(() => shuffleMC(q, idx), [q, idx]);

  // Defensive fallback: if idx ever ends up past the end of the session (shouldn't
  // happen now that goNext is clamped above), bail out gracefully instead of
  // throwing mid-render and blanking the whole screen.
  if (!q) return null;

  const selected = answers[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.module;
  const isLast = idx === questions.length - 1;
  const wasCorrect = selected !== null && selected === q.answer;
  const wasWrong = selected !== null && selected !== q.answer;


  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#0891b2', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>🎧 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#cffafe', borderRadius: '99px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#0891b2', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      {stage === 'listen' ? (
        <>
          {!speechSupported ? (
            <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '16px', padding: '18px', marginBottom: '16px', fontSize: '0.85rem', fontWeight: '600', color: '#9a3412', textAlign: 'center' }}>
              {gs.noSpeechSupport}
            </div>
          ) : (
            <div style={{ background: 'linear-gradient(135deg,#ecfeff,#cffafe)', border: '2px solid #0891b230', borderRadius: '20px', padding: '32px 24px', marginBottom: '16px', textAlign: 'center' }}>
              <button onClick={play} disabled={speaking} style={{ width: '96px', height: '96px', borderRadius: '50%', background: speaking ? '#67e8f9' : 'linear-gradient(135deg,#0891b2,#0e7490)', border: 'none', color: 'white', fontSize: '2.4rem', cursor: speaking ? 'default' : 'pointer', boxShadow: '0 8px 28px rgba(8,145,178,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', transition: 'all 0.2s' }}>
                {speaking ? '🔊' : '▶️'}
              </button>
              <div style={{ fontWeight: '800', color: '#0e7490', fontSize: '0.95rem', marginBottom: '6px' }}>{hasPlayed ? gs.listenAgainLabel : gs.listenLabel}</div>
              <div style={{ fontSize: '0.78rem', color: '#0891b2', fontWeight: '600' }}>{gs.listenInstructions}</div>
            </div>
          )}
          <button onClick={() => setStage('question')} disabled={!hasPlayed && speechSupported} style={{ width: '100%', padding: '16px', background: (hasPlayed || !speechSupported) ? 'linear-gradient(135deg,#0891b2,#0e7490)' : '#f1f5f9', color: (hasPlayed || !speechSupported) ? 'white' : '#94a3b8', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: (hasPlayed || !speechSupported) ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: (hasPlayed || !speechSupported) ? '0 4px 16px rgba(8,145,178,0.3)' : 'none' }}>
            {gs.continueToQuestionListening}
          </button>
        </>
      ) : (
        <>
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '18px 20px', marginBottom: '16px' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.answerQuestionLabel}</div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', lineHeight: 1.5 }}>{q.question}</div>
          </div>
          {speechSupported && (
            <button onClick={play} disabled={speaking} style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 16px', padding: '10px 16px', background: 'white', border: '1.5px solid #cffafe', borderRadius: '50px', color: '#0e7490', fontWeight: '800', fontSize: '0.8rem', cursor: speaking ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {speaking ? '🔊' : '🔁'} {gs.listenAgainLabel}
            </button>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
            {mc.options.map((opt, i) => {
              const originalIdx = mc.order[i];
              let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
              if (originalIdx === selected) {
                if (wasCorrect) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
                else { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
              }
              return <button key={i} onClick={() => pick(originalIdx)} disabled={selected !== null} style={{ padding: '15px 18px', background: bg, border, borderRadius: '14px', fontWeight: '700', fontSize: '0.88rem', color, cursor: selected !== null ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s', textAlign: 'left' }}>
                <span style={{ fontWeight: '800', color: '#94a3b8', marginRight: '8px' }}>{String.fromCharCode(65 + i)}.</span>{opt}
              </button>;
            })}
          </div>
          {selected !== null && <FeedbackBanner correct={wasCorrect} gs={gs} />}
          {wasCorrect && <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>💡 {gs.lang === 'en' ? (q.tip_en || q.tip) : q.tip}</div>}
          {wasWrong && <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#9a3412', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>🔎 {gs.rereadHint}</div>}
        </>
      )}

      {stage === 'question' && <SessionFooter idx={idx} isLast={isLast} answered={selected !== null} color="#0891b2" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />}
    </div>
  );
}

function ConjugateGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null));

  const pick = (optIdx) => {
    const next = [...answers];
    next[idx] = optIdx;
    setAnswers(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = answers.map((a, i) => i).filter(i => answers[i] !== null);
    const score = answeredIdxs.filter(i => answers[i] === questions[i].answer).length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: answers[i] === questions[i].answer })));
  };

  const q = questions[idx];
  const mc = useMemo(() => shuffleMC(q, idx), [q, idx]);

  // Defensive fallback: if idx ever ends up past the end of the session, bail out
  // gracefully instead of throwing mid-render and blanking the whole screen.
  if (!q) return null;
  const selected = answers[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.topic || q.module;
  const isLast = idx === questions.length - 1;

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#9333ea', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📘 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#f3e8ff', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#9333ea', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ background: '#faf5ff', border: '2px solid #9333ea30', borderRadius: '20px', padding: '24px', marginBottom: '20px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#9333ea', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>{gs.conjugateLabel}</div>
        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.6 }}>
          {q.sentence.split('_____').map((part, i, arr) => (
            <span key={i}>{part}{i < arr.length - 1 && <span style={{ background: selected !== null ? (selected === q.answer ? '#bbf7d0' : '#fecdd3') : '#f3e8ff', borderBottom: '2px solid #9333ea', padding: '0 12px', borderRadius: '6px', fontWeight: '900', color: selected !== null ? (selected === q.answer ? '#15803d' : '#be123c') : '#9333ea' }}>{selected !== null ? q.options[q.answer] : '  ?  '}</span>}</span>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        {mc.options.map((opt, i) => {
          const originalIdx = mc.order[i];
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (selected !== null) {
            if (originalIdx === q.answer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            else if (originalIdx === selected) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
          }
          return <button key={i} onClick={() => pick(originalIdx)} style={{ padding: '16px 12px', background: bg, border, borderRadius: '14px', fontWeight: '700', fontSize: '0.9rem', color, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s', textAlign: 'center' }}>{opt}</button>;
        })}
      </div>
      {selected !== null && <FeedbackBanner correct={selected === q.answer} gs={gs} />}
      {selected !== null && <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>💡 {gs.lang === 'en' ? (q.tip_en || q.tip) : q.tip}</div>}
      <SessionFooter idx={idx} isLast={isLast} answered={selected !== null} color="#9333ea" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

function PrepositionGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null));

  const pick = (optIdx) => {
    const next = [...answers];
    next[idx] = optIdx;
    setAnswers(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = answers.map((a, i) => i).filter(i => answers[i] !== null);
    const score = answeredIdxs.filter(i => answers[i] === questions[i].answer).length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: answers[i] === questions[i].answer })));
  };

  const q = questions[idx];
  const mc = useMemo(() => shuffleMC(q, idx), [q, idx]);

  // Defensive fallback: if idx ever ends up past the end of the session, bail out
  // gracefully instead of throwing mid-render and blanking the whole screen.
  if (!q) return null;
  const selected = answers[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.topic || q.module;
  const isLast = idx === questions.length - 1;

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#ea580c', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📘 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#ffedd5', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#ea580c', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ background: '#fff7ed', border: '2px solid #ea580c30', borderRadius: '20px', padding: '24px', marginBottom: '20px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#ea580c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '14px' }}>{gs.prepositionLabel}</div>
        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.6 }}>
          {q.sentence.split('_____').map((part, i, arr) => (
            <span key={i}>{part}{i < arr.length - 1 && <span style={{ background: selected !== null ? (selected === q.answer ? '#bbf7d0' : '#fecdd3') : '#ffedd5', borderBottom: '2px solid #ea580c', padding: '0 12px', borderRadius: '6px', fontWeight: '900', color: selected !== null ? (selected === q.answer ? '#15803d' : '#be123c') : '#ea580c' }}>{selected !== null ? q.options[q.answer] : '  ?  '}</span>}</span>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        {mc.options.map((opt, i) => {
          const originalIdx = mc.order[i];
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (selected !== null) {
            if (originalIdx === q.answer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            else if (originalIdx === selected) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
          }
          return <button key={i} onClick={() => pick(originalIdx)} style={{ padding: '16px 12px', background: bg, border, borderRadius: '14px', fontWeight: '700', fontSize: '0.9rem', color, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s', textAlign: 'center' }}>{opt}</button>;
        })}
      </div>
      {selected !== null && <FeedbackBanner correct={selected === q.answer} gs={gs} />}
      {selected !== null && <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>💡 {gs.lang === 'en' ? (q.tip_en || q.tip) : q.tip}</div>}
      <SessionFooter idx={idx} isLast={isLast} answered={selected !== null} color="#ea580c" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

// ── WRITING (production activity) ─────────────────────────────────────────────
// Student writes a free-text response, then reveals a model answer plus a short
// checklist of what a strong answer includes, and self-rates. There's no single
// "correct" answer to check programmatically, so scoring is self-assessed —
// "good" or "excellent" counts as correct for the score screen and coverage.
function WritingGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [responses, setResponses] = useState(() => Array(questions.length).fill(''));
  const [ratings, setRatings] = useState(() => Array(questions.length).fill(null));
  const [revealed, setRevealed] = useState(false);

  useEffect(() => { setRevealed(false); }, [idx]);

  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = ratings.map((a, i) => i).filter(i => ratings[i] !== null);
    const score = answeredIdxs.filter(i => ratings[i] === 'good' || ratings[i] === 'excellent').length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: ratings[i] === 'good' || ratings[i] === 'excellent' })));
  };

  const q = questions[idx];
  if (!q) return null;

  const response = responses[idx];
  const rating = ratings[idx];
  const wordCount = response.trim() ? response.trim().split(/\s+/).length : 0;
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.module;
  const isLast = idx === questions.length - 1;
  const keyPoints = gs.lang === 'en' ? (q.keyPointsEn || q.keyPoints) : q.keyPoints;

  const setResponse = (value) => {
    const next = [...responses];
    next[idx] = value;
    setResponses(next);
  };
  const rate = (value) => {
    const next = [...ratings];
    next[idx] = value;
    setRatings(next);
  };

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#c026d3', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📝 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#fae8ff', borderRadius: '99px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#c026d3', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      <div style={{ background: '#fdf4ff', border: '2px solid #c026d330', borderRadius: '20px', padding: '20px', marginBottom: '16px' }}>
        <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#c026d3', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.writingPromptLabel}</div>
        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.5 }}>{q.prompt}</div>
        {gs.lang !== 'es' && <div style={{ fontSize: '0.8rem', color: '#86198f', fontWeight: '600', marginTop: '8px', fontStyle: 'italic' }}>{q.promptEn}</div>}
      </div>

      <textarea
        value={response}
        onChange={e => setResponse(e.target.value)}
        placeholder={gs.writingPlaceholder}
        disabled={revealed}
        rows={6}
        style={{ width: '100%', boxSizing: 'border-box', padding: '14px', borderRadius: '14px', border: '1.5px solid #e2e8f0', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.92rem', color: '#0f172a', resize: 'vertical', outline: 'none', marginBottom: '8px', background: revealed ? '#f8fafc' : 'white' }}
      />
      <div style={{ fontSize: '0.72rem', fontWeight: '700', color: wordCount >= (q.minWords || 0) ? '#16a34a' : '#94a3b8', marginBottom: '16px' }}>
        {wordCount} {gs.wordCountLabel} {q.minWords ? `(${q.minWords} ${gs.minWordsHint})` : ''}
      </div>

      {!revealed ? (
        <button onClick={() => setRevealed(true)} disabled={wordCount === 0} style={{ width: '100%', padding: '16px', background: wordCount > 0 ? 'linear-gradient(135deg,#c026d3,#a21caf)' : '#f1f5f9', color: wordCount > 0 ? 'white' : '#94a3b8', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: wordCount > 0 ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: wordCount > 0 ? '0 4px 16px rgba(192,38,211,0.3)' : 'none', marginBottom: '16px' }}>
          {gs.checkMyWriting}
        </button>
      ) : (
        <>
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '18px 20px', marginBottom: '14px' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.modelAnswerLabel}</div>
            <div style={{ fontSize: '0.92rem', fontWeight: '600', color: '#0f172a', lineHeight: 1.6, fontStyle: 'italic' }}>{q.modelAnswer}</div>
          </div>
          <div style={{ background: '#fdf4ff', border: '1.5px solid #f5d0fe', borderRadius: '16px', padding: '16px 18px', marginBottom: '18px' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#a21caf', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.keyPointsLabel}</div>
            {(keyPoints || []).map((kp, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', color: '#475569', fontWeight: '600', marginBottom: '6px', lineHeight: 1.4 }}>
                <span>☐</span><span>{kp}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#0f172a', marginBottom: '10px', textAlign: 'center' }}>{gs.selfRateLabel}</div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {[['needs', gs.rateNeedsPractice, '#f97316'], ['good', gs.rateGood, '#0284c7'], ['excellent', gs.rateExcellent, '#16a34a']].map(([val, label, color]) => (
              <button key={val} onClick={() => rate(val)} style={{ flex: 1, padding: '12px 8px', background: rating === val ? color : 'white', color: rating === val ? 'white' : '#475569', border: `1.5px solid ${rating === val ? color : '#e2e8f0'}`, borderRadius: '12px', fontWeight: '700', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      <SessionFooter idx={idx} isLast={isLast} answered={rating !== null} color="#c026d3" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

// ── SPEAKING (production activity) ────────────────────────────────────────────
// Student records themselves via the browser's microphone (MediaRecorder API),
// plays it back, listens to a spoken model answer (via speech synthesis, same
// mechanism as Listening), and self-rates. Gracefully degrades if the mic isn't
// available or permission is denied — never blocks the rest of the session.
function SpeakingGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [ratings, setRatings] = useState(() => Array(questions.length).fill(null));
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [micError, setMicError] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const isMounted = useRef(true);

  const micSupported = typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) && typeof MediaRecorder !== 'undefined';
  const speechSupported = typeof window !== 'undefined' && !!window.speechSynthesis;

  useEffect(() => {
    setRecordingUrl(null); setIsRecording(false); setMicError(false);
  }, [idx]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (speechSupported) window.speechSynthesis.cancel();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch (e) { /* already stopped */ }
      }
    };
  }, [speechSupported]);

  const startRecording = async () => {
    if (!micSupported) return;
    setMicError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (isMounted.current) setRecordingUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (e) {
      if (isMounted.current) setMicError(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const playModel = () => {
    if (!speechSupported) return;
    const q = questions[idx];
    if (!q) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(q.modelText);
    utterance.lang = 'es-ES';
    utterance.rate = (q.level === 'A1' || q.level === 'A2') ? 0.85 : 0.95;
    utterance.onstart = () => { if (isMounted.current) setSpeaking(true); };
    utterance.onend = () => { if (isMounted.current) setSpeaking(false); };
    utterance.onerror = () => { if (isMounted.current) setSpeaking(false); };
    window.speechSynthesis.speak(utterance);
  };

  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = ratings.map((a, i) => i).filter(i => ratings[i] !== null);
    const score = answeredIdxs.filter(i => ratings[i] === 'good' || ratings[i] === 'excellent').length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: ratings[i] === 'good' || ratings[i] === 'excellent' })));
  };

  const q = questions[idx];
  if (!q) return null;

  const rating = ratings[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.module;
  const isLast = idx === questions.length - 1;
  const keyPoints = gs.lang === 'en' ? (q.keyPointsEn || q.keyPoints) : q.keyPoints;

  const rate = (value) => {
    const next = [...ratings];
    next[idx] = value;
    setRatings(next);
  };

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#dc2626', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>🎙️ {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#fee2e2', borderRadius: '99px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#dc2626', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      <div style={{ background: '#fef2f2', border: '2px solid #dc262630', borderRadius: '20px', padding: '20px', marginBottom: '16px' }}>
        <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#dc2626', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.speakingPromptLabel}</div>
        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.5 }}>{q.prompt}</div>
        {gs.lang !== 'es' && <div style={{ fontSize: '0.8rem', color: '#991b1b', fontWeight: '600', marginTop: '8px', fontStyle: 'italic' }}>{q.promptEn}</div>}
      </div>

      {!micSupported ? (
        <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '16px', padding: '18px', marginBottom: '16px', fontSize: '0.85rem', fontWeight: '600', color: '#9a3412', textAlign: 'center' }}>
          {gs.noMicSupport}
        </div>
      ) : (
        <div style={{ background: 'white', border: '2px solid #fecaca', borderRadius: '20px', padding: '24px', marginBottom: '16px', textAlign: 'center' }}>
          {micError && <div style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: '700', marginBottom: '12px' }}>{gs.micPermissionDenied}</div>}
          <button onClick={isRecording ? stopRecording : startRecording} style={{ width: '96px', height: '96px', borderRadius: '50%', background: isRecording ? '#fca5a5' : 'linear-gradient(135deg,#dc2626,#b91c1c)', border: 'none', color: 'white', fontSize: '2.2rem', cursor: 'pointer', boxShadow: '0 8px 28px rgba(220,38,38,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', transition: 'all 0.2s' }}>
            {isRecording ? '⬛' : '🎙️'}
          </button>
          <div style={{ fontWeight: '800', color: '#991b1b', fontSize: '0.9rem' }}>{isRecording ? gs.recording : (recordingUrl ? gs.recordAgain : gs.startRecording)}</div>
          {recordingUrl && !isRecording && (
            <audio src={recordingUrl} controls style={{ marginTop: '16px', width: '100%' }} />
          )}
        </div>
      )}

      {speechSupported && (
        <button onClick={playModel} disabled={speaking} style={{ width: '100%', padding: '14px', background: 'white', border: '1.5px solid #fecaca', borderRadius: '14px', color: '#991b1b', fontWeight: '800', fontSize: '0.85rem', cursor: speaking ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '16px' }}>
          {speaking ? '🔊 …' : gs.playModelAnswer}
        </button>
      )}

      {(recordingUrl || !micSupported) && (
        <>
          <div style={{ background: '#fdf4ff', border: '1.5px solid #f5d0fe', borderRadius: '16px', padding: '16px 18px', marginBottom: '18px' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#a21caf', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.keyPointsLabel}</div>
            {(keyPoints || []).map((kp, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', color: '#475569', fontWeight: '600', marginBottom: '6px', lineHeight: 1.4 }}>
                <span>☐</span><span>{kp}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#0f172a', marginBottom: '10px', textAlign: 'center' }}>{gs.selfRateLabel}</div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {[['needs', gs.rateNeedsPractice, '#f97316'], ['good', gs.rateGood, '#0284c7'], ['excellent', gs.rateExcellent, '#16a34a']].map(([val, label, color]) => (
              <button key={val} onClick={() => rate(val)} style={{ flex: 1, padding: '12px 8px', background: rating === val ? color : 'white', color: rating === val ? 'white' : '#475569', border: `1.5px solid ${rating === val ? color : '#e2e8f0'}`, borderRadius: '12px', fontWeight: '700', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      <SessionFooter idx={idx} isLast={isLast} answered={rating !== null} color="#dc2626" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

// ── DICTATION (dictado) ───────────────────────────────────────────────────────
// Student listens (replayable, via speech synthesis) and types exactly what
// they hear, then gets automatic word-by-word feedback against the original —
// no self-rating needed, this one is objectively checkable.
function DictationGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(''));
  const [checked, setChecked] = useState(() => Array(questions.length).fill(false));
  const [speaking, setSpeaking] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const isMounted = useRef(true);
  const speechSupported = typeof window !== 'undefined' && !!window.speechSynthesis;

  useEffect(() => { setHasPlayed(false); }, [idx]);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; if (speechSupported) window.speechSynthesis.cancel(); };
  }, [speechSupported]);

  const q = questions[idx];
  const comparison = useMemo(() => (q ? compareDictation(q.text, answers[idx]) : null), [q, answers, idx]);

  if (!q) return null;

  const isChecked = checked[idx];

  const play = () => {
    if (!speechSupported) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(q.text);
    utterance.lang = 'es-ES';
    utterance.rate = (q.level === 'A1' || q.level === 'A2') ? 0.8 : 0.9;
    utterance.onstart = () => { if (isMounted.current) setSpeaking(true); };
    utterance.onend = () => { if (isMounted.current) setSpeaking(false); };
    utterance.onerror = () => { if (isMounted.current) setSpeaking(false); };
    window.speechSynthesis.speak(utterance);
    setHasPlayed(true);
  };

  const setAnswer = (value) => {
    const next = [...answers];
    next[idx] = value;
    setAnswers(next);
  };
  const check = () => {
    const next = [...checked];
    next[idx] = true;
    setChecked(next);
  };

  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const checkedIdxs = checked.map((c, i) => i).filter(i => checked[i]);
    const results = checkedIdxs.map(i => compareDictation(questions[i].text, answers[i]));
    const score = results.filter(r => r.ratio >= 0.8).length;
    onComplete(score, checkedIdxs.length, checkedIdxs.map(i => questions[i].id), checkedIdxs.map((i, ri) => ({ id: questions[i].id, correct: results[ri].ratio >= 0.8 })));
  };

  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.module;
  const isLast = idx === questions.length - 1;

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#0d9488', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>🎧✏️ {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#ccfbf1', borderRadius: '99px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#0d9488', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      <div style={{ background: '#f0fdfa', border: '2px solid #0d948830', borderRadius: '20px', padding: '28px 24px', marginBottom: '16px', textAlign: 'center' }}>
        {!speechSupported ? (
          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#9a3412' }}>{gs.noSpeechSupport}</div>
        ) : (
          <>
            <button onClick={play} disabled={speaking} style={{ width: '88px', height: '88px', borderRadius: '50%', background: speaking ? '#5eead4' : 'linear-gradient(135deg,#0d9488,#0f766e)', border: 'none', color: 'white', fontSize: '2.1rem', cursor: speaking ? 'default' : 'pointer', boxShadow: '0 8px 28px rgba(13,148,136,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              {speaking ? '🔊' : '▶️'}
            </button>
            <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0f766e', marginBottom: '4px' }}>{hasPlayed ? gs.playDictationAgain : gs.playDictation}</div>
            <div style={{ fontSize: '0.75rem', color: '#0d9488', fontWeight: '600' }}>{gs.dictationInstructions}</div>
          </>
        )}
      </div>

      <textarea
        value={answers[idx]}
        onChange={e => setAnswer(e.target.value)}
        disabled={isChecked}
        rows={2}
        placeholder="…"
        style={{ width: '100%', boxSizing: 'border-box', padding: '14px', borderRadius: '14px', border: '1.5px solid #e2e8f0', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.95rem', color: '#0f172a', resize: 'vertical', outline: 'none', marginBottom: '12px', background: isChecked ? '#f8fafc' : 'white' }}
      />

      {!isChecked ? (
        <button onClick={check} disabled={!answers[idx].trim()} style={{ width: '100%', padding: '16px', background: answers[idx].trim() ? 'linear-gradient(135deg,#0d9488,#0f766e)' : '#f1f5f9', color: answers[idx].trim() ? 'white' : '#94a3b8', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: answers[idx].trim() ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '16px' }}>
          {gs.checkDictation}
        </button>
      ) : (
        <>
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '16px 18px', marginBottom: '12px', lineHeight: 1.8 }}>
            {comparison.results.map((r, i) => (
              <span key={i} style={{ color: r.correct ? '#15803d' : '#be123c', fontWeight: '700', marginRight: '6px', textDecoration: r.correct ? 'none' : 'underline wavy #be123c' }}>{r.word}</span>
            ))}
          </div>
          <FeedbackBanner correct={comparison.ratio >= 0.8} gs={gs} />
          <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#64748b', textAlign: 'center', margin: '8px 0 16px' }}>{comparison.correctCount}/{comparison.total} {gs.wordsCorrect}</div>
          <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>{gs.dictationOriginal}</div>
            <div style={{ fontSize: '0.88rem', fontWeight: '600', color: '#0f172a' }}>{q.text}</div>
          </div>
        </>
      )}

      <SessionFooter idx={idx} isLast={isLast} answered={isChecked} color="#0d9488" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

// ── TRANSLATION (traducción) ──────────────────────────────────────────────────
// Student sees a sentence in their own interface language and types the
// Spanish translation, then compares against a model answer and self-rates
// (translations often have more than one valid phrasing, so this isn't
// auto-scored like Dictation).
function TranslationGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [responses, setResponses] = useState(() => Array(questions.length).fill(''));
  const [ratings, setRatings] = useState(() => Array(questions.length).fill(null));
  const [revealed, setRevealed] = useState(false);

  useEffect(() => { setRevealed(false); }, [idx]);

  const q = questions[idx];
  if (!q) return null;

  const response = responses[idx];
  const rating = ratings[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.module;
  const isLast = idx === questions.length - 1;
  const sourceText = gs.lang === 'pt' ? q.sourcePt : q.sourceEn;

  const setResponse = (value) => {
    const next = [...responses];
    next[idx] = value;
    setResponses(next);
  };
  const rate = (value) => {
    const next = [...ratings];
    next[idx] = value;
    setRatings(next);
  };

  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = ratings.map((a, i) => i).filter(i => ratings[i] !== null);
    const score = answeredIdxs.filter(i => ratings[i] === 'good' || ratings[i] === 'excellent').length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: ratings[i] === 'good' || ratings[i] === 'excellent' })));
  };

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#c2410c', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>🔁 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#ffedd5', borderRadius: '99px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#c2410c', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      <div style={{ background: '#fff7ed', border: '2px solid #c2410c30', borderRadius: '20px', padding: '20px', marginBottom: '16px' }}>
        <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#c2410c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.translationSourceLabel}</div>
        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.5 }}>{sourceText}</div>
      </div>

      <textarea
        value={response}
        onChange={e => setResponse(e.target.value)}
        placeholder={gs.translationPlaceholder}
        disabled={revealed}
        rows={2}
        style={{ width: '100%', boxSizing: 'border-box', padding: '14px', borderRadius: '14px', border: '1.5px solid #e2e8f0', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.95rem', color: '#0f172a', resize: 'vertical', outline: 'none', marginBottom: '16px', background: revealed ? '#f8fafc' : 'white' }}
      />

      {!revealed ? (
        <button onClick={() => setRevealed(true)} disabled={!response.trim()} style={{ width: '100%', padding: '16px', background: response.trim() ? 'linear-gradient(135deg,#c2410c,#7c2d12)' : '#f1f5f9', color: response.trim() ? 'white' : '#94a3b8', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: response.trim() ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '16px' }}>
          {gs.checkTranslation}
        </button>
      ) : (
        <>
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '18px 20px', marginBottom: '18px' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.modelAnswerLabel}</div>
            <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>{q.modelAnswer}</div>
          </div>
          <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#0f172a', marginBottom: '10px', textAlign: 'center' }}>{gs.selfRateLabel}</div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {[['needs', gs.rateNeedsPractice, '#f97316'], ['good', gs.rateGood, '#0284c7'], ['excellent', gs.rateExcellent, '#16a34a']].map(([val, label, color]) => (
              <button key={val} onClick={() => rate(val)} style={{ flex: 1, padding: '12px 8px', background: rating === val ? color : 'white', color: rating === val ? 'white' : '#475569', border: `1.5px solid ${rating === val ? color : '#e2e8f0'}`, borderRadius: '12px', fontWeight: '700', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      <SessionFooter idx={idx} isLast={isLast} answered={rating !== null} color="#c2410c" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

function WordBuilderGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [built, setBuilt] = useState([]);
  const [remaining, setRemaining] = useState([]);
  const [checked, setChecked] = useState(false);
  const [correct, setCorrect] = useState(null);
  const [results, setResults] = useState(() => Array(questions.length).fill(null));

  useEffect(() => {
    if (questions[idx]) {
      const shuffled = seededShuffle([...questions[idx].words], getDayOfYear() + idx * 7);
      setRemaining(shuffled.map((w, i) => ({ w, id: i })));
      setBuilt([]); setChecked(false); setCorrect(null);
    }
  }, [idx, questions]);

  const tap = (item) => { if (checked) return; setRemaining(r => r.filter(x => x.id !== item.id)); setBuilt(b => [...b, item]); };
  const remove = (item) => { if (checked) return; setBuilt(b => b.filter(x => x.id !== item.id)); setRemaining(r => [...r, item]); };
  const check = () => {
    const q = questions[idx];
  if (!q) return null;
    const isCorrect = built.map(x => x.w).join(' ') === q.words.join(' ');
    setCorrect(isCorrect); setChecked(true);
    const next = [...results]; next[idx] = isCorrect; setResults(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const checkedIdxs = results.map((r, i) => i).filter(i => results[i] !== null);
    const score = checkedIdxs.filter(i => results[i]).length;
    onComplete(score, checkedIdxs.length, checkedIdxs.map(i => questions[i].id), checkedIdxs.map(i => ({ id: questions[i].id, correct: !!results[i] })));
  };

  const q = questions[idx];
  if (!q) return null;
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.topic || q.module;
  const isLast = idx === questions.length - 1;

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#16a34a', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📘 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#dcfce7', borderRadius: '99px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#16a34a', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ background: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: '18px', padding: '18px', marginBottom: '16px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#16a34a', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.translateToSpanish}</div>
        <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '1rem', lineHeight: 1.5 }}>{gs.lang === 'en' ? (q.en || q.pt) : q.pt}</div>
      </div>
      <div style={{ minHeight: '64px', background: checked ? (correct ? '#f0fdf4' : '#fff1f2') : '#f8fafc', border: `2px ${checked ? 'solid' : 'dashed'} ${checked ? (correct ? '#86efac' : '#fecdd3') : '#cbd5e1'}`, borderRadius: '16px', padding: '12px 16px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {built.length === 0 && !checked && <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '600' }}>{gs.tapWords}</span>}
        {built.map(item => (
          <button key={item.id} onClick={() => remove(item)} style={{ background: checked ? 'transparent' : '#dc2626', color: checked ? (correct ? '#15803d' : '#be123c') : 'white', border: 'none', borderRadius: '8px', padding: '8px 14px', fontWeight: '800', fontSize: '0.9rem', cursor: checked ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{item.w}</button>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
        {remaining.map(item => (
          <button key={item.id} onClick={() => tap(item)} style={{ background: 'white', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '10px 16px', fontWeight: '700', fontSize: '0.9rem', color: '#0f172a', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{item.w}</button>
        ))}
      </div>
      {!checked && <button onClick={check} disabled={built.length === 0} style={{ width: '100%', padding: '16px', background: built.length > 0 ? 'linear-gradient(135deg,#16a34a,#15803d)' : '#f1f5f9', color: built.length > 0 ? 'white' : '#94a3b8', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: built.length > 0 ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '16px' }}>{gs.verify}</button>}
      {checked && <FeedbackBanner correct={correct} gs={gs} />}
      {checked && !correct && (
        <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: '14px', padding: '14px 16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#15803d', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>{gs.correctAnswerLabel}</div>
          <div style={{ fontWeight: '800', color: '#15803d', fontSize: '1rem', lineHeight: 1.5 }}>{q.words.join(' ')}</div>
        </div>
      )}
      <SessionFooter idx={idx} isLast={isLast} answered={checked} color="#16a34a" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

function OddOutGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null));

  const pick = (word) => {
    const next = [...answers];
    next[idx] = word;
    setAnswers(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = answers.map((a, i) => i).filter(i => answers[i] !== null);
    const score = answeredIdxs.filter(i => answers[i] === questions[i].answer).length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: answers[i] === questions[i].answer })));
  };

  const q = questions[idx];
  if (!q) return null;
  const selected = answers[idx];
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.topic || q.module;
  const isLast = idx === questions.length - 1;

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#d97706', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📘 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#fef3c7', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#d97706', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#d97706', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '6px' }}>{gs.whichDoesntBelong}</div>
        <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>{gs.identify}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        {q.words.map(word => {
          const isAnswer = word === q.answer;
          const isSelected = word === selected;
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (selected) {
            if (isAnswer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            if (isSelected && !isAnswer) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
            if (!isAnswer && !isSelected) { bg = '#f8fafc'; color = '#94a3b8'; }
          }
          return <button key={word} onClick={() => pick(word)} style={{ padding: '20px 12px', background: bg, border, borderRadius: '16px', fontWeight: '800', fontSize: '1rem', color, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.2s', textAlign: 'center' }}>{word}</button>;
        })}
      </div>
      {selected && <FeedbackBanner correct={selected === q.answer} gs={gs} />}
      {selected && <div style={{ background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>💡 {gs.lang === 'en' ? (q.reason_en || q.reason) : q.reason}</div>}
      <SessionFooter idx={idx} isLast={isLast} answered={!!selected} color="#d97706" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

function ModismoGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null));

  const q = questions[idx];
  if (!q) return null;
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.topic || q.module;
  const opts = gs.lang === 'en' ? (q.options_en || q.options) : q.options;
  const correct = gs.lang === 'en' ? (q.en || q.pt) : q.pt;
  const shuffledOpts = seededShuffle(opts, getDayOfYear() + idx * 13);
  const selected = answers[idx];
  const isLast = idx === questions.length - 1;

  const pick = (opt) => {
    const next = [...answers];
    next[idx] = opt;
    setAnswers(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = questions.map((iq, i) => i).filter(i => answers[i] !== null);
    const perItem = answeredIdxs.map(i => {
      const iCorrect = gs.lang === 'en' ? (questions[i].en || questions[i].pt) : questions[i].pt;
      return { id: questions[i].id, correct: answers[i] === iCorrect };
    });
    const score = perItem.filter(x => x.correct).length;
    onComplete(score, perItem.length, perItem.map(x => x.id), perItem);
  };

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#be185d', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📘 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#fce7f3', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#be185d', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ background: 'linear-gradient(135deg,#fdf2f8,#fce7f3)', border: '2px solid #f9a8d4', borderRadius: '20px', padding: '28px', textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#be185d', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>{gs.whatMeans}</div>
        <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#1e1b4b', lineHeight: 1.4 }}>"{q.verb}"</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
        {shuffledOpts.map(opt => {
          const isAnswer = opt === correct;
          const isSelected = opt === selected;
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (selected) {
            if (isAnswer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            else if (isSelected) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
            else { bg = '#f8fafc'; color = '#94a3b8'; }
          }
          return <button key={opt} onClick={() => pick(opt)} style={{ padding: '16px 20px', background: bg, border, borderRadius: '14px', fontWeight: '700', fontSize: '0.9rem', color, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.2s', textAlign: 'left' }}>{opt}</button>;
        })}
      </div>
      {selected && <FeedbackBanner correct={selected === correct} gs={gs} />}
      <SessionFooter idx={idx} isLast={isLast} answered={!!selected} color="#be185d" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}


function VerdFalsoGame({ questions, onComplete, gs, previouslySeenIds = new Set(), poolTotal = 0 }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(questions.length).fill(null));

  const pick = (ans) => {
    const next = [...answers];
    next[idx] = ans;
    setAnswers(next);
  };
  const goNext = () => setIdx(i => Math.min(i + 1, questions.length - 1));
  const goBack = () => setIdx(i => Math.max(i - 1, 0));
  const finish = () => {
    const answeredIdxs = answers.map((a, i) => i).filter(i => answers[i] !== null);
    const score = answeredIdxs.filter(i => answers[i] === questions[i].answer).length;
    onComplete(score, answeredIdxs.length, answeredIdxs.map(i => questions[i].id), answeredIdxs.map(i => ({ id: questions[i].id, correct: answers[i] === questions[i].answer })));
  };

  const q = questions[idx];
  if (!q) return null;
  const seenSoFar = new Set([...previouslySeenIds, ...questions.slice(0, idx + 1).map(x => x.id)]);
  const curriculumTag = q.topic || q.module;
  const selected = answers[idx];
  const answered = selected !== null;
  const wasCorrect = answered && selected === q.answer;
  const isLast = idx === questions.length - 1;

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#0f766e', display: 'flex', justifyContent: 'space-between' }}>
        <span>{idx + 1} / {questions.length}</span>
      </div>
      {poolTotal > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{poolTotal > 0 ? Math.round((seenSoFar.size / poolTotal) * 100) : 0}% {gs.lang === 'en' ? 'seen at this level' : gs.lang === 'pt' ? 'visto neste nível' : 'visto en este nivel'}</span>
          {curriculumTag && <span style={{ background: '#f1f5f9', borderRadius: '50px', padding: '2px 10px', fontSize: '0.65rem', fontWeight: '800' }}>📘 {curriculumTag}</span>}
        </div>
      )}
      <div style={{ height: '6px', background: '#ccfbf1', borderRadius: '99px', marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / questions.length) * 100}%`, background: '#0f766e', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ background: 'linear-gradient(135deg,#f0fdfa,#ccfbf1)', border: '2px solid #99f6e4', borderRadius: '20px', padding: '28px 24px', marginBottom: '24px', textAlign: 'center' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#0f766e', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '14px' }}>
          {gs.lang === 'en' ? 'True or False?' : gs.lang === 'pt' ? 'Verdadeiro ou Falso?' : '¿Verdadero o Falso?'}
        </div>
        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.6 }}>"{gs.lang === 'en' ? q.stmt_en : q.stmt}"</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        {[true, false].map(ans => {
          const label = ans
            ? (gs.lang === 'en' ? '✅ True' : gs.lang === 'pt' ? '✅ Verdadeiro' : '✅ Verdadero')
            : (gs.lang === 'en' ? '❌ False' : gs.lang === 'pt' ? '❌ Falso' : '❌ Falso');
          let bg = 'white', border = '2px solid #e2e8f0', color = '#0f172a';
          if (answered) {
            if (ans === q.answer) { bg = '#f0fdf4'; border = '2px solid #86efac'; color = '#15803d'; }
            else if (ans === selected) { bg = '#fff1f2'; border = '2px solid #fecdd3'; color = '#be123c'; }
            else { bg = '#f8fafc'; color = '#94a3b8'; border = '2px solid #f1f5f9'; }
          }
          return (
            <button key={String(ans)} onClick={() => pick(ans)} style={{ padding: '22px 12px', background: bg, border, borderRadius: '16px', fontWeight: '800', fontSize: '1rem', color, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.2s', textAlign: 'center' }}>
              {label}
            </button>
          );
        })}
      </div>
      {answered && <FeedbackBanner correct={wasCorrect} gs={gs} />}
      {answered && (
        <div style={{ background: wasCorrect ? '#f0fdf4' : '#fff1f2', border: `1.5px solid ${wasCorrect ? '#86efac' : '#fecdd3'}`, borderRadius: '14px', padding: '14px 16px', fontSize: '0.82rem', color: wasCorrect ? '#15803d' : '#be123c', fontWeight: '600', lineHeight: 1.5, marginBottom: '16px' }}>
          💡 {gs.lang === 'en' ? q.exp_en : q.exp}
        </div>
      )}
      <SessionFooter idx={idx} isLast={isLast} answered={answered} color="#0f766e" gs={gs} onBack={goBack} onNext={goNext} onFinish={finish} />
    </div>
  );
}

function ScoreScreen({ score, total, gameType, onSave, saving, saved, gs, onReplay, onMenu }) {
  const pct = Math.round((score / total) * 100);
  const info = GAME_INFO[gameType];
  const msgMap = { en: ['🏆 Perfect!','🔥 Very good!','👍 Good work!','💪 Keep practising!'], pt: ['🏆 Perfeito!','🔥 Muito bem!','👍 Bom trabalho!','💪 Continue praticando!'], es: ['🏆 ¡Perfecto!','🔥 ¡Muy bien!','👍 ¡Buen trabajo!','💪 ¡Sigue practicando!'] };
  const msgs = msgMap[gs?.lang || 'es'];
  const msg = pct === 100 ? msgs[0] : pct >= 80 ? msgs[1] : pct >= 60 ? msgs[2] : msgs[3];
  const wrong = total - score;

  return (
    <div style={{ textAlign: 'center', padding: '8px 0' }}>
      <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: info.bg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.8rem', margin: '0 auto 20px', boxShadow: `0 12px 36px ${info.color}40` }}>{info.icon}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#0f172a', marginBottom: '4px' }}>{msg}</div>
      <div style={{ fontSize: '3.5rem', fontWeight: '900', color: info.color, lineHeight: 1, margin: '16px 0 6px' }}>{score}<span style={{ fontSize: '1.5rem', color: '#94a3b8' }}>/{total}</span></div>
      <div style={{ fontSize: '1rem', fontWeight: '700', color: '#64748b', marginBottom: '20px' }}>{pct}% {gs.accuracyLabel}</div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: '50px', padding: '6px 14px' }}>
          <span>✅</span><span style={{ fontWeight: '800', color: '#15803d', fontSize: '0.85rem' }}>{score} {gs.correct_count}</span>
        </div>
        {wrong > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff1f2', border: '1.5px solid #fecdd3', borderRadius: '50px', padding: '6px 14px' }}>
            <span>❌</span><span style={{ fontWeight: '800', color: '#be123c', fontSize: '0.85rem' }}>{wrong}</span>
          </div>
        )}
      </div>

      {!saved ? (
        <button onClick={onSave} disabled={saving} style={{ width: '100%', padding: '16px', background: saving ? '#f1f5f9' : info.bg, color: saving ? '#94a3b8' : 'white', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '1rem', cursor: saving ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: saving ? 'none' : `0 4px 16px ${info.color}40`, marginBottom: '12px' }}>
          {saving ? gs.saving : gs.saveResult}
        </button>
      ) : (
        <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: '14px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '12px' }}>
          <span style={{ fontSize: '1.3rem' }}>✅</span>
          <span style={{ fontWeight: '800', color: '#15803d' }}>{gs.saved}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={onReplay} style={{ flex: 1, padding: '15px', background: info.bg, color: 'white', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: `0 4px 16px ${info.color}40` }}>
          {gs.playAgainSession}
        </button>
        <button onClick={onMenu} style={{ flex: 1, padding: '15px', background: 'white', border: '1.5px solid #e2e8f0', borderRadius: '14px', fontWeight: '800', fontSize: '0.88rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {gs.backMenu}
        </button>
      </div>
    </div>
  );
}

// ── GRAMMAR SECTION COMPONENTS ────────────────────────────────────────────────
// ── STUDY PLAN (structured weekly curriculum) ─────────────────────────────────
// Turns "browse freely" into an assigned weekly plan: 12 themed weeks per CEFR
// level, each with a handful of tasks across different game types (including
// writing/speaking from week 2-3 onward). Progress is stored per student+level
// in localStorage (no backend schema change needed) and is purely a pacing
// layer — it doesn't gate or restrict free practice, it just gives structure
// to students who want it instead of a flat list of 13 unordered games.
function useStudyPlanProgress(studentId, level) {
  const storageKey = `studyplan_${studentId}_${level}`;
  const [done, setDone] = useState(() => {
    if (typeof window === 'undefined' || !level) return {};
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch (e) { return {}; }
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !level) return;
    try { setDone(JSON.parse(localStorage.getItem(storageKey)) || {}); } catch (e) { setDone({}); }
  }, [storageKey, level]);
  const toggleTask = (taskKey) => {
    const next = { ...done, [taskKey]: !done[taskKey] };
    setDone(next);
    if (typeof window !== 'undefined') localStorage.setItem(storageKey, JSON.stringify(next));
  };
  return [done, toggleTask];
}

function StudyPlanLevelPicker({ gs, onPick }) {
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontWeight: '900', fontSize: '1.3rem', color: '#0f172a', marginBottom: '6px' }}>{gs.planChooseLevel}</div>
      <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', marginBottom: '24px' }}>{gs.planChooseLevelSub}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {CEFR_ORDER.map(lvl => {
          const meta = CEFR_META[lvl];
          return (
            <button key={lvl} onClick={() => onPick(lvl)} style={{ padding: '18px', background: meta.color, color: 'white', border: 'none', borderRadius: '16px', fontWeight: '900', fontSize: '1rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: `0 6px 20px ${meta.color}40` }}>
              {cefrLabel(lvl, gs)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StudyPlanView({ level, gs, session, onChangeLevel, onLaunchTask }) {
  const weeks = CURRICULUM[level] || [];
  const [done, toggleTask] = useStudyPlanProgress(session.id, level);

  const weekComplete = (week) => week.tasks.every((t, i) => done[`${week.week}-${i}`]);
  const currentWeekIdx = weeks.findIndex(w => !weekComplete(w));
  const currentWeek = currentWeekIdx === -1 ? weeks.length : currentWeekIdx + 1;
  const totalTasks = weeks.reduce((sum, w) => sum + w.tasks.length, 0);
  const doneTasks = weeks.reduce((sum, w) => sum + w.tasks.filter((t, i) => done[`${w.week}-${i}`]).length, 0);

  const weekTitle = (week) => (gs.lang === 'en' ? week.titleEn : gs.lang === 'pt' ? week.titlePt : week.title);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <button onClick={onChangeLevel} style={{ background: 'none', border: 'none', color: '#94a3b8', fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', padding: 0 }}>{gs.planChangeLevel}</button>
        <span style={{ background: PRACTICE_LEVEL_STYLE[level].bg, color: PRACTICE_LEVEL_STYLE[level].color, borderRadius: '50px', padding: '4px 12px', fontSize: '0.72rem', fontWeight: '800' }}>{levelLabel(level, gs)}</span>
      </div>

      <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontWeight: '800', fontSize: '0.85rem', color: '#0f172a' }}>{gs.planWeekLabel} {Math.min(currentWeek, weeks.length)} / {weeks.length}</div>
          <div style={{ fontWeight: '800', fontSize: '0.78rem', color: '#64748b' }}>{doneTasks}/{totalTasks} {gs.planTasksDone}</div>
        </div>
        <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${totalTasks > 0 ? (doneTasks / totalTasks) * 100 : 0}%`, background: PRACTICE_LEVEL_STYLE[level].bar, borderRadius: '99px', transition: 'width 0.3s' }} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {weeks.map((week, wi) => {
          const isCurrent = week.week === currentWeek;
          const complete = weekComplete(week);
          return (
            <div key={week.week} style={{ background: 'white', border: `1.5px solid ${isCurrent ? PRACTICE_LEVEL_STYLE[level].color : '#f1f5f9'}`, borderRadius: '16px', padding: '16px', opacity: complete ? 0.7 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: '900', color: PRACTICE_LEVEL_STYLE[level].color, background: PRACTICE_LEVEL_STYLE[level].bg, borderRadius: '50px', padding: '3px 10px', flexShrink: 0 }}>{gs.planWeekLabel} {week.week}</span>
                <div style={{ fontWeight: '800', fontSize: '0.88rem', color: '#0f172a', flex: 1 }}>{weekTitle(week)}</div>
                {complete && <span style={{ fontSize: '1.1rem' }}>✅</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {week.tasks.map((task, ti) => {
                  const g = GAME_INFO[task.type];
                  const taskKey = `${week.week}-${ti}`;
                  const isDone = !!done[taskKey];
                  return (
                    <div key={ti} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: isDone ? '#f0fdf4' : '#fafafa', borderRadius: '10px' }}>
                      <button onClick={() => toggleTask(taskKey)} style={{ width: '22px', height: '22px', borderRadius: '6px', border: `1.5px solid ${isDone ? '#16a34a' : '#cbd5e1'}`, background: isDone ? '#16a34a' : 'white', color: 'white', fontSize: '0.75rem', fontWeight: '900', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isDone ? '✓' : ''}
                      </button>
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>{g.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '700', fontSize: '0.8rem', color: isDone ? '#16a34a' : '#0f172a' }}>{g.name}</div>
                        <div style={{ fontSize: '0.68rem', fontWeight: '600', color: '#94a3b8' }}>{task.count} {gs.planItemsTarget}</div>
                      </div>
                      <button onClick={() => onLaunchTask(task.type, level)} style={{ background: g.color, color: 'white', border: 'none', borderRadius: '10px', padding: '6px 12px', fontWeight: '800', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', flexShrink: 0 }}>
                        {gs.planStartTask}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GrammarLevelPicker({ gs, onPick }) {
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontWeight: '900', fontSize: '1.3rem', color: '#0f172a', marginBottom: '20px' }}>{gs.grammarChooseLevel}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {CEFR_ORDER.map(lvl => {
          const meta = CEFR_META[lvl];
          return (
            <button key={lvl} onClick={() => onPick(lvl)} style={{ padding: '18px', background: meta.color, color: 'white', border: 'none', borderRadius: '16px', fontWeight: '900', fontSize: '1rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: `0 6px 20px ${meta.color}40` }}>
              {cefrLabel(lvl, gs)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GrammarTopicList({ level, gs, completedTopicIds, onChangeLevel, onSelectTopic }) {
  const topics = GRAMMAR_TOPICS.filter(t => t.level === level);
  const byModule = {};
  topics.forEach(t => { (byModule[t.module] = byModule[t.module] || []).push(t); });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <button onClick={onChangeLevel} style={{ background: 'none', border: 'none', color: '#94a3b8', fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', padding: 0 }}>{gs.grammarChangeLevel}</button>
        <span style={{ background: PRACTICE_LEVEL_STYLE[level].bg, color: PRACTICE_LEVEL_STYLE[level].color, borderRadius: '50px', padding: '4px 12px', fontSize: '0.72rem', fontWeight: '800' }}>
          {levelLabel(level, gs)}
        </span>
      </div>
      <div style={{ fontWeight: '900', fontSize: '1.15rem', color: '#0f172a', marginBottom: '16px' }}>{gs.grammarTopics}</div>
      {Object.entries(byModule).map(([mod, mtopics]) => (
        <div key={mod} style={{ marginBottom: '18px' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>{mod}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {mtopics.map(t => {
              const done = completedTopicIds.has(t.id);
              return (
                <button key={t.id} onClick={() => onSelectTopic(t)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: done ? '#faf5ff' : 'white', border: '1.5px solid #f1f5f9', borderRadius: '14px', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', textAlign: 'left' }}>
                  <span style={{ fontSize: '1.4rem', flexShrink: 0, width: '34px', height: '34px', background: '#f3e8ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📖</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.9rem' }}>{t.title}</div>
                    <div style={{ fontWeight: '600', color: '#94a3b8', fontSize: '0.72rem', marginTop: '1px' }}>{t.questions.length} {gs.questions}</div>
                  </div>
                  {done && <span style={{ color: '#9333ea', fontWeight: '800', fontSize: '0.9rem' }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function GrammarQuiz({ topic, gs, onFinish, onBack }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => Array(topic.questions.length).fill(null));
  const [inputVal, setInputVal] = useState('');

  const q = topic.questions[idx];
  const currentAnswer = answers[idx];
  const mc = useMemo(() => (q && q.type === 'mcq' ? shuffleMC(q, idx) : null), [q, idx]);

  // Defensive fallback: if idx ever ends up past the end of the topic, bail out
  // gracefully instead of throwing mid-render and blanking the whole screen.
  if (!q) return null;

  const setAnswer = (value) => {
    const next = [...answers];
    next[idx] = value;
    setAnswers(next);
  };

  const goTo = (newIdx) => {
    const clamped = Math.max(0, Math.min(newIdx, topic.questions.length - 1));
    setInputVal(answers[clamped] !== null && topic.questions[clamped].type === 'fill' ? answers[clamped] : '');
    setIdx(clamped);
  };

  const isLast = idx === topic.questions.length - 1;

  return (
    <div>
      <button onClick={onBack} style={{ display: 'block', marginBottom: '16px', background: 'none', border: 'none', color: '#94a3b8', fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', padding: 0 }}>{gs.grammarBackTopics}</button>
      <div style={{ fontWeight: '900', fontSize: '1.1rem', color: '#0f172a', marginBottom: '6px' }}>{topic.title}</div>
      <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: '600', marginBottom: '16px', lineHeight: 1.5 }}>{topic.explanation}</div>
      <div style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: '#9333ea' }}>{idx + 1} / {topic.questions.length}</div>
      <div style={{ height: '6px', background: '#f3e8ff', borderRadius: '99px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / topic.questions.length) * 100}%`, background: '#9333ea', borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>

      <div style={{ background: '#faf5ff', border: '2px solid #e9d5ff', borderRadius: '20px', padding: '22px', marginBottom: '20px' }}>
        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.6, marginBottom: q.type === 'mcq' ? '0' : '16px' }}>{q.prompt}</div>

        {q.type === 'mcq' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
            {mc.options.map((opt, i) => {
              const originalIdx = mc.order[i];
              const isPicked = currentAnswer === originalIdx;
              return (
                <button key={i} onClick={() => setAnswer(originalIdx)} style={{ padding: '12px 16px', textAlign: 'left', background: isPicked ? '#9333ea' : 'white', color: isPicked ? 'white' : '#0f172a', border: `2px solid ${isPicked ? '#9333ea' : '#e2e8f0'}`, borderRadius: '12px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {opt}
                </button>
              );
            })}
          </div>
        )}

        {q.type === 'fill' && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              placeholder="..."
              style={{ flex: 1, padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.9rem', outline: 'none' }}
            />
            <button onClick={() => setAnswer(inputVal)} style={{ padding: '12px 18px', background: '#9333ea', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.grammarCheck}</button>
          </div>
        )}

        {currentAnswer !== null && (
          <div style={{ marginTop: '14px', background: 'white', border: '1.5px solid #e9d5ff', borderRadius: '12px', padding: '12px 14px', fontSize: '0.8rem', color: '#6d28d9', fontWeight: '600', lineHeight: 1.5 }}>
            💡 {q.tip}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        {idx > 0 && (
          <button onClick={() => goTo(idx - 1)} style={{ flex: 1, padding: '14px', background: 'white', border: '1.5px solid #e2e8f0', borderRadius: '14px', fontWeight: '800', fontSize: '0.85rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.grammarBack}</button>
        )}
        {!isLast ? (
          <button onClick={() => goTo(idx + 1)} style={{ flex: 2, padding: '14px', background: '#9333ea', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.85rem', color: 'white', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.grammarNext}</button>
        ) : (
          <button onClick={() => onFinish(answers)} style={{ flex: 2, padding: '14px', background: '#16a34a', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.85rem', color: 'white', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.grammarFinish}</button>
        )}
      </div>
    </div>
  );
}

function GrammarReview({ topic, answers, gs, saving, onDone }) {
  const isCorrect = (q, given) => {
    if (given === null) return false;
    if (q.type === 'mcq') return given === q.answer;
    return normalizeAnswer(given) === normalizeAnswer(q.answer);
  };
  const correctCount = topic.questions.reduce((sum, q, i) => sum + (isCorrect(q, answers[i]) ? 1 : 0), 0);

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <div style={{ fontWeight: '900', fontSize: '1.2rem', color: '#0f172a', marginBottom: '4px' }}>{gs.grammarReviewTitle}: {topic.title}</div>
        <div style={{ fontSize: '2.2rem', fontWeight: '900', color: '#9333ea' }}>{correctCount}<span style={{ fontSize: '1.1rem', color: '#94a3b8' }}>/{topic.questions.length}</span></div>
        {saving && <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', marginTop: '6px' }}>{gs.saving}</div>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
        {topic.questions.map((q, i) => {
          const given = answers[i];
          const correct = isCorrect(q, given);
          const givenText = given === null ? '—' : (q.type === 'mcq' ? q.options[given] : given);
          const correctText = q.type === 'mcq' ? q.options[q.answer] : q.answer;
          return (
            <div key={i} style={{ background: correct ? '#f0fdf4' : '#fff1f2', border: `1.5px solid ${correct ? '#86efac' : '#fecdd3'}`, borderRadius: '14px', padding: '14px 16px' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>{correct ? '✅' : '❌'} {q.prompt}</div>
              <div style={{ fontSize: '0.78rem', color: '#475569', fontWeight: '600' }}>{gs.grammarYourAnswer} <span style={{ fontWeight: '800' }}>{givenText}</span></div>
              {!correct && <div style={{ fontSize: '0.78rem', color: '#475569', fontWeight: '600', marginTop: '2px' }}>{gs.grammarCorrectAnswer} <span style={{ fontWeight: '800', color: '#15803d' }}>{correctText}</span></div>}
              <div style={{ fontSize: '0.75rem', color: '#6d28d9', fontWeight: '600', marginTop: '6px' }}>💡 {q.tip}</div>
            </div>
          );
        })}
      </div>

      <button onClick={onDone} disabled={saving} style={{ width: '100%', padding: '14px', background: saving ? '#e9d5ff' : '#9333ea', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.9rem', color: 'white', cursor: saving ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.grammarDone}</button>
    </div>
  );
}

function GrammarSection({ session, gs }) {
  const [level, setLevel] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem(`grammar_level_${session.id}`) : null));
  const [view, setView] = useState('topics'); // 'topics' | 'quiz' | 'review'
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [lastAnswers, setLastAnswers] = useState(null);
  const [completedTopicIds, setCompletedTopicIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!level) return;
    supabase
      .from('game_completions')
      .select('game_type')
      .eq('student_id', session.id)
      .like('game_type', `grammar:${level}:%`)
      .then(({ data }) => {
        const ids = new Set((data || []).map(row => row.game_type.split(':')[2]));
        setCompletedTopicIds(ids);
      });
  }, [level, session.id]);

  const pickLevel = (lvl) => {
    setLevel(lvl);
    if (typeof window !== 'undefined') localStorage.setItem(`grammar_level_${session.id}`, lvl);
  };

  const selectTopic = (topic) => { setSelectedTopic(topic); setView('quiz'); };

  const finishQuiz = async (answers) => {
    setLastAnswers(answers);
    setView('review');
    const isCorrect = (q, given) => {
      if (given === null) return false;
      if (q.type === 'mcq') return given === q.answer;
      return normalizeAnswer(given) === normalizeAnswer(q.answer);
    };
    const correctCount = selectedTopic.questions.reduce((sum, q, i) => sum + (isCorrect(q, answers[i]) ? 1 : 0), 0);
    setSaving(true);
    await supabase.from('game_completions').insert({
      student_id: session.id,
      game_type: `grammar:${level}:${selectedTopic.id}`,
      score: correctCount,
      total: selectedTopic.questions.length,
      played_date: todayISO(),
    });
    setSaving(false);
    setCompletedTopicIds(prev => new Set([...prev, selectedTopic.id]));
  };

  if (!level) return <GrammarLevelPicker gs={gs} onPick={pickLevel} />;
  if (view === 'quiz' && selectedTopic) return <GrammarQuiz topic={selectedTopic} gs={gs} onFinish={finishQuiz} onBack={() => setView('topics')} />;
  if (view === 'review' && selectedTopic && lastAnswers) return <GrammarReview topic={selectedTopic} answers={lastAnswers} gs={gs} saving={saving} onDone={() => setView('topics')} />;
  return <GrammarTopicList level={level} gs={gs} completedTopicIds={completedTopicIds} onChangeLevel={() => setLevel(null)} onSelectTopic={selectTopic} />;
}



const TYPE_COLORS = {
  film:    { bg: '#fef3c7', color: '#d97706', icon: '🎬' },
  series:  { bg: '#ede9fe', color: '#7c3aed', icon: '📺' },
  book:    { bg: '#dcfce7', color: '#16a34a', icon: '📚' },
  podcast: { bg: '#dbeafe', color: '#1d4ed8', icon: '🎙️' },
};

// CEFR level metadata shared by the Practice game picker, the Grammar picker, and any
// place a level badge is shown — colors match the Level Test for a consistent system
// across the whole platform (A1 green → C2 amber).
const CEFR_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CEFR_META = {
  A1: { color: '#16a34a', bg: '#dcfce7', sublabel: { en: 'Beginner', pt: 'Iniciante', es: 'Principiante' } },
  A2: { color: '#22b8a0', bg: '#d0f5ee', sublabel: { en: 'Elementary', pt: 'Elementar', es: 'Elemental' } },
  B1: { color: '#0284c7', bg: '#dbeafe', sublabel: { en: 'Intermediate', pt: 'Intermediário', es: 'Intermedio' } },
  B2: { color: '#4f46e5', bg: '#e0e7ff', sublabel: { en: 'Upper Intermediate', pt: 'Intermediário Alto', es: 'Intermedio Alto' } },
  C1: { color: '#7c3aed', bg: '#ede9fe', sublabel: { en: 'Advanced', pt: 'Avançado', es: 'Avanzado' } },
  C2: { color: '#b45309', bg: '#fef3c7', sublabel: { en: 'Proficiency', pt: 'Proficiência', es: 'Maestría' } },
};
function cefrLabel(level, gs) {
  const meta = CEFR_META[level] || CEFR_META.A1;
  return `${level} · ${meta.sublabel[gs.lang] || meta.sublabel.es}`;
}

const LEVEL_COLORS = {
  Beginner:     { bg: '#dbeafe', color: '#1d4ed8' },
  Intermediate: { bg: '#ede9fe', color: '#7c3aed' },
  Both:         { bg: '#f0fdf4', color: '#16a34a' },
};

// Used specifically by the Grammar/Game level-picker (practiceLevel), keyed by CEFR
// level to match the raw 'level' values stored on content items and completions.
const PRACTICE_LEVEL_STYLE = {
  A1: { bg: CEFR_META.A1.bg, color: CEFR_META.A1.color, bar: CEFR_META.A1.color },
  A2: { bg: CEFR_META.A2.bg, color: CEFR_META.A2.color, bar: CEFR_META.A2.color },
  B1: { bg: CEFR_META.B1.bg, color: CEFR_META.B1.color, bar: CEFR_META.B1.color },
  B2: { bg: CEFR_META.B2.bg, color: CEFR_META.B2.color, bar: CEFR_META.B2.color },
  C1: { bg: CEFR_META.C1.bg, color: CEFR_META.C1.color, bar: CEFR_META.C1.color },
  C2: { bg: CEFR_META.C2.bg, color: CEFR_META.C2.color, bar: CEFR_META.C2.color },
};
function levelLabel(level, gs) {
  return cefrLabel(level, gs);
}

function MediaTab({ gs }) {
  const [filter, setFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');

  const types = ['all', 'film', 'series', 'book', 'podcast'];
  const typeLabels = { all: gs.mediaAll, film: gs.mediaFilms, series: gs.mediaSeries, book: gs.mediaBooks, podcast: gs.mediaPodcasts };
  const levelLabels = { all: gs.mediaAll, Beginner: gs.mediaBeginner, Intermediate: gs.mediaIntermediate, Both: gs.mediaBoth };

  const filtered = MEDIA_DATA.filter(m => {
    const matchType = filter === 'all' || m.type === filter;
    const matchLevel = levelFilter === 'all' || m.level === levelFilter || m.level === 'Both';
    return matchType && matchLevel;
  });

  const lang = gs.lang || 'es';

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '16px' }}>
          {lang === 'es' ? '🎬 Películas, series, libros y podcasts para aprender español' : lang === 'en' ? '🎬 Films, series, books and podcasts to learn Spanish' : '🎬 Filmes, séries, livros e podcasts para aprender espanhol'}
        </div>
        {/* Type filter */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
          {types.map(t => (
            <button key={t} onClick={() => setFilter(t)} style={{ padding: '6px 14px', borderRadius: '50px', border: '1.5px solid', borderColor: filter === t ? '#0f172a' : '#e2e8f0', background: filter === t ? '#0f172a' : 'white', color: filter === t ? 'white' : '#64748b', fontWeight: '700', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', transition: 'all 0.15s' }}>
              {t !== 'all' && TYPE_COLORS[t] ? TYPE_COLORS[t].icon + ' ' : ''}{typeLabels[t]}
            </button>
          ))}
        </div>
        {/* Level filter */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['all', 'Beginner', 'Intermediate'].map(l => (
            <button key={l} onClick={() => setLevelFilter(l)} style={{ padding: '5px 12px', borderRadius: '50px', border: '1.5px solid', borderColor: levelFilter === l ? (LEVEL_COLORS[l] || {color:'#0f172a'}).color : '#e2e8f0', background: levelFilter === l ? (LEVEL_COLORS[l] || {bg:'#0f172a'}).bg : 'white', color: levelFilter === l ? (LEVEL_COLORS[l] || {color:'#0f172a'}).color : '#94a3b8', fontWeight: '700', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', transition: 'all 0.15s' }}>
              {levelLabels[l]}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {filtered.map((m, i) => {
          const tc = TYPE_COLORS[m.type];
          const lc = LEVEL_COLORS[m.level] || LEVEL_COLORS.Both;
          return (
            <div key={i} style={{ background: '#fafafa', border: '1.5px solid #f1f5f9', borderRadius: '16px', padding: '16px', transition: 'box-shadow 0.15s' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
                <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: tc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>{tc.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '900', fontSize: '0.95rem', color: '#0f172a' }}>{m.title}</span>
                    <span style={{ fontSize: '0.9rem' }}>{m.variant}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ background: tc.bg, color: tc.color, borderRadius: '50px', padding: '2px 8px', fontSize: '0.65rem', fontWeight: '800' }}>{typeLabels[m.type]}</span>
                    <span style={{ background: lc.bg, color: lc.color, borderRadius: '50px', padding: '2px 8px', fontSize: '0.65rem', fontWeight: '800' }}>{levelLabels[m.level]}</span>
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#475569', fontWeight: '600', lineHeight: 1.5, marginBottom: '8px' }}>
                <span style={{ fontWeight: '800', color: '#0f172a' }}>{gs.mediaWhy} </span>{m.why[lang] || m.why.es}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600' }}>
                <span style={{ fontWeight: '800', color: '#64748b' }}>{gs.mediaWhere} </span>{m.where}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.72rem', color: '#cbd5e1', fontWeight: '600' }}>
        {filtered.length} {lang === 'es' ? 'recomendaciones' : lang === 'en' ? 'recommendations' : 'recomendações'}
      </div>
    </div>
  );
}

// ── MAIN PRACTICE ES COMPONENT ────────────────────────────────────────────────
export default function PracticeES({ session, onBack, lang = 'en' }) {
  const gs = { ...GAME_STRINGS[lang] || GAME_STRINGS['en'], lang };
  const [tab, setTab] = useState('game');
  const [practiceLevel, setPracticeLevel] = useState(null); // null = show level picker; asked fresh every time (not persisted)
  const [planLevel, setPlanLevel] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem(`studyplan_level_${session.id}`) : null));
  const choosePlanLevel = (lvl) => {
    setPlanLevel(lvl);
    if (typeof window !== 'undefined') localStorage.setItem(`studyplan_level_${session.id}`, lvl);
  };
  const launchPlanTask = (type, level) => {
    setSelectedGame(type);
    setPracticeLevel(level);
    setSaved(false);
    setGameState('intro');
    setTab('game');
  };
  const [gameState, setGameState] = useState('menu');
  const [selectedGame, setSelectedGame] = useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [sessionQuestionIds, setSessionQuestionIds] = useState([]);
  const [sessionItemResults, setSessionItemResults] = useState([]);
  const [sessionNonce, setSessionNonce] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [allCompletions, setAllCompletions] = useState([]);
  const [gameStreak, setGameStreak] = useState(0);
  const [totalGames, setTotalGames] = useState(0);
  const [missionDone, setMissionDone] = useState(false);
  const [allMissions, setAllMissions] = useState([]);
  const [missionSaving, setMissionSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const defaultGame = GAME_TYPES[0]; // neutral fallback shown only before the student picks anything
  const todayPhrase = getTodayPhrase();
  const currentMission = getCurrentMission();
  const weekNum = getWeekNumber();
  const yearNum = new Date().getFullYear();

  const loadData = useCallback(async () => {
    setLoadingData(true);
    const [{ data: comps }, { data: missions }] = await Promise.all([
      supabase.from('game_completions').select('played_date, game_type, question_ids, item_results').eq('student_id', session.id).order('played_date', { ascending: false }),
      supabase.from('mission_completions').select('*').eq('student_id', session.id).order('created_at', { ascending: false }),
    ]);
    if (comps) {
      setAllCompletions(comps);
      setTotalGames(comps.length);
      const dates = [...new Set(comps.map(c => c.played_date))].sort().reverse();
      const today = todayISO();
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      if (dates.length && (dates[0] === today || dates[0] === yesterday)) {
        let s = 1;
        for (let i = 1; i < dates.length; i++) {
          const diff = (new Date(dates[i-1]) - new Date(dates[i])) / 86400000;
          if (diff === 1) s++; else break;
        }
        setGameStreak(s);
      } else { setGameStreak(0); }
    }
    const thisMission = (missions || []).find(m => m.week_number === weekNum && m.year === yearNum);
    setMissionDone(!!thisMission);
    setAllMissions(missions || []);
    setLoadingData(false);
  }, [session.id, weekNum, yearNum]);

  useEffect(() => { loadData(); }, [loadData]);

  // Games available at the chosen level, and progress within that level (all-time, level-tagged completions only —
  // completions saved before this update won't carry a level tag and won't count here).
  const availableGames = practiceLevel ? gamesAvailableAtLevel(practiceLevel) : [];

  // Real coverage: which specific questions has the student ever seen, per game type at this level —
  // not just "did they open this activity once." Falls back to an empty set for completions saved
  // before question_ids existed (those don't count toward coverage).
  const seenIdsForType = (type, level) => {
    if (!level) return new Set();
    const ids = new Set();
    allCompletions
      .filter(c => c.game_type === `${type}:${level}`)
      .forEach(c => (c.question_ids || []).forEach(id => ids.add(id)));
    return ids;
  };
  const poolTotalForType = (type, level) => (level ? (POOLS[type] || []).filter(item => item.level === level).length : 0);

  const overallSeen = practiceLevel ? availableGames.reduce((sum, t) => sum + seenIdsForType(t, practiceLevel).size, 0) : 0;
  const overallTotal = practiceLevel ? availableGames.reduce((sum, t) => sum + poolTotalForType(t, practiceLevel), 0) : 0;

  const previouslySeenIds = useMemo(() => {
    if (!practiceLevel) return new Set();
    const ids = new Set();
    allCompletions
      .filter(c => c.game_type === `${selectedGame}:${practiceLevel}`)
      .forEach(c => (c.question_ids || []).forEach(id => ids.add(id)));
    return ids;
  }, [selectedGame, practiceLevel, allCompletions]);
  const poolTotal = practiceLevel ? poolTotalForType(selectedGame, practiceLevel) : 0;
  const itemSchedule = useMemo(() => computeItemSchedule(allCompletions), [allCompletions]);

  const handleGameComplete = (s, t, qIds, itemResults) => {
    setScore(s); setTotal(t); setSessionQuestionIds(qIds || []); setSessionItemResults(itemResults || []); setGameState('score');
  };

  const saveResult = async () => {
    setSaving(true);
    await supabase.from('game_completions').insert({ student_id: session.id, game_type: `${selectedGame}:${practiceLevel}`, score, total, played_date: todayISO(), question_ids: sessionQuestionIds, item_results: sessionItemResults });
    setSaving(false); setSaved(true);
    loadData();
  };

  const saveMission = async () => {
    setMissionSaving(true);
    await supabase.from('mission_completions').insert({ student_id: session.id, mission_text: currentMission.text, week_number: weekNum, year: yearNum });
    setMissionSaving(false); setMissionDone(true);
  };

  const questions = useMemo(
    () => getSessionQuestions(selectedGame || defaultGame, practiceLevel || 'A1', previouslySeenIds, itemSchedule, sessionNonce),
    [selectedGame, practiceLevel, sessionNonce, defaultGame, previouslySeenIds, itemSchedule]
  );
  const info = GAME_INFO[selectedGame || defaultGame];
  const startOrReplay = () => { setSessionNonce(n => n + 1); setGameState('playing'); };

  if (loadingData) return <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontWeight: '600' }}>Cargando...</div>;

  return (
    <>
      <style>{practiceStyles}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button onClick={() => onBack()} style={{ background: '#f1f5f9', border: 'none', borderRadius: '12px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', whiteSpace: 'nowrap' }}>← Portal</button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontWeight: '900', fontSize: '1.2rem', color: '#0f172a', lineHeight: 1 }}>{gs.practiceTitle}</div>
          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', marginTop: '3px' }}>{gs.practiceSubtitle}</div>
        </div>
        {gameStreak > 0 && <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '50px', padding: '5px 12px', fontSize: '0.75rem', fontWeight: '800', color: '#ea580c', whiteSpace: 'nowrap' }}>🔥 {gameStreak}d</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', background: '#f1f5f9', borderRadius: '16px', padding: '5px', marginBottom: '24px' }}>
        {[['game', '🎮', gs.game], ['plan', '🗓️', gs.planTab], ['phrase', '💬', gs.phrase], ['mission', '🌍', gs.mission], ['media', '🎬', gs.mediaTab], ['grammar', '📖', gs.grammarTab]].map(([key, icon, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: '12px 6px', background: tab === key ? 'white' : 'transparent', border: 'none', borderRadius: '12px', fontWeight: '800', fontSize: '0.82rem', color: tab === key ? '#0f172a' : '#64748b', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: tab === key ? '0 2px 8px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}>
            {icon} {label}
          </button>
        ))}
      </div>

      {tab === 'game' && (
        <div className="practice-card">
          {gameState === 'menu' && !practiceLevel && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontWeight: '900', fontSize: '1.3rem', color: '#0f172a', marginBottom: '6px' }}>{gs.chooseLevelTitle}</div>
              <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', marginBottom: '24px' }}>{gs.chooseLevelSub}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {CEFR_ORDER.map(lvl => {
                  const meta = CEFR_META[lvl];
                  return (
                    <button key={lvl} onClick={() => setPracticeLevel(lvl)} style={{ padding: '18px 10px', background: meta.color, color: 'white', border: 'none', borderRadius: '16px', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: `0 6px 20px ${meta.color}40`, textAlign: 'center' }}>
                      <div style={{ fontWeight: '900', fontSize: '1.2rem', lineHeight: 1 }}>{lvl}</div>
                      <div style={{ fontWeight: '700', fontSize: '0.7rem', marginTop: '4px', opacity: 0.85 }}>{meta.sublabel[gs.lang] || meta.sublabel.es}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {gameState === 'menu' && practiceLevel && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <button onClick={() => setPracticeLevel(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', padding: 0 }}>{gs.changeLevel}</button>
                <span style={{ background: PRACTICE_LEVEL_STYLE[practiceLevel].bg, color: PRACTICE_LEVEL_STYLE[practiceLevel].color, borderRadius: '50px', padding: '4px 12px', fontSize: '0.72rem', fontWeight: '800' }}>
                  {levelLabel(practiceLevel, gs)}
                </span>
              </div>

              {availableGames.length > 0 && (
                <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                    <span>{overallSeen} / {overallTotal} {gs.progressAtLevel}</span>
                    <span>{overallTotal > 0 ? Math.round((overallSeen / overallTotal) * 100) : 0}%</span>
                  </div>
                  <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${overallTotal > 0 ? (overallSeen / overallTotal) * 100 : 0}%`, background: PRACTICE_LEVEL_STYLE[practiceLevel].bar, borderRadius: '99px', transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}

              {availableGames.length === 0 && (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontWeight: '600', fontSize: '0.85rem' }}>{gs.noContentLevel}</div>
              )}

              {(() => {
                const coreTypes = availableGames.filter(t => t === 'vocabulary' || t === 'reading' || t === 'listening');
                const productionTypes = availableGames.filter(t => t === 'writing' || t === 'speaking');
                const gameTypes = availableGames.filter(t => t !== 'vocabulary' && t !== 'reading' && t !== 'listening' && t !== 'writing' && t !== 'speaking');

                const renderCard = (type, isCore) => {
                  const g = GAME_INFO[type];
                  const typeSeen = seenIdsForType(type, practiceLevel).size;
                  const typeTotal = poolTotalForType(type, practiceLevel);
                  const typePct = typeTotal > 0 ? Math.round((typeSeen / typeTotal) * 100) : 0;
                  return (
                    <button key={type} onClick={() => { setSelectedGame(type); setSaved(false); setGameState('intro'); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: isCore ? `linear-gradient(135deg, ${g.color}0d, ${g.color}05)` : 'white', border: `1.5px solid ${isCore ? g.color + '30' : '#f1f5f9'}`, borderRadius: '14px', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', textAlign: 'left', transition: 'all 0.15s', position: 'relative' }}>
                      <span style={{ fontSize: '1.6rem', flexShrink: 0, width: '36px', height: '36px', background: g.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{g.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.9rem' }}>{g.name}</div>
                          {isCore && <span style={{ background: g.color, color: 'white', borderRadius: '50px', padding: '1px 7px', fontSize: '0.58rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>{gs.newBadge}</span>}
                        </div>
                        <div style={{ fontWeight: '600', color: '#94a3b8', fontSize: '0.75rem', marginTop: '1px' }}>{g.desc[gs.lang] || g.desc.es}</div>
                      </div>
                      <span style={{ fontSize: '0.72rem', fontWeight: '800', color: g.color, flexShrink: 0 }}>{typePct}%</span>
                    </button>
                  );
                };

                return (
                  <>
                    {coreTypes.length > 0 && (
                      <div style={{ marginBottom: '22px' }}>
                        <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '2px' }}>📚 {gs.coreExercisesLabel}</div>
                        <div style={{ fontSize: '0.72rem', fontWeight: '600', color: '#cbd5e1', marginBottom: '10px' }}>{gs.coreExercisesSub}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {coreTypes.map(type => renderCard(type, true))}
                        </div>
                      </div>
                    )}
                    {productionTypes.length > 0 && (
                      <div style={{ marginBottom: '22px' }}>
                        <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '2px' }}>🎤 {gs.productionLabel}</div>
                        <div style={{ fontSize: '0.72rem', fontWeight: '600', color: '#cbd5e1', marginBottom: '10px' }}>{gs.productionSub}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {productionTypes.map(type => renderCard(type, true))}
                        </div>
                      </div>
                    )}
                    {gameTypes.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>🎮 {gs.otherGamesLabel}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {gameTypes.map(type => renderCard(type, false))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              {gameStreak > 0 && (
                <div style={{ marginTop: '16px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1.3rem' }}>🔥</span>
                  <div style={{ fontWeight: '700', color: '#ea580c', fontSize: '0.85rem' }}>{gameStreak} {gs.daysStreak} · {totalGames} {gs.gamesTotal}</div>
                </div>
              )}
            </div>
          )}

          {gameState === 'intro' && info && (
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => setGameState('menu')} style={{ display: 'block', marginBottom: '20px', background: 'none', border: 'none', color: '#94a3b8', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{gs.chooseOther}</button>
              <div style={{ width: '90px', height: '90px', borderRadius: '24px', background: info.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', margin: '0 auto 20px', boxShadow: `0 12px 36px ${info.color}35` }}>{info.icon}</div>
              <div style={{ fontSize: '0.7rem', fontWeight: '800', color: info.color, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '6px' }}>{gs.freeLabel}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#0f172a', marginBottom: '6px' }}>{info.name}</div>
              <div style={{ fontSize: '0.88rem', color: '#64748b', fontWeight: '600', marginBottom: '24px' }}>{info.desc[gs.lang] || info.desc.es}</div>
              <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '16px', marginBottom: '28px', display: 'flex', justifyContent: 'center', gap: '32px' }}>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#0f172a' }}>{questions.length}</div><div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{gs.questions}</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#0f172a' }}>~5 min</div><div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{gs.duration}</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '1.5rem', fontWeight: '900', color: Math.max(poolTotal - previouslySeenIds.size, 0) > 0 ? '#16a34a' : '#94a3b8' }}>{Math.max(poolTotal - previouslySeenIds.size, 0)}</div><div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{gs.newCardsLabel}</div></div>
              </div>
              <button onClick={startOrReplay} style={{ width: '100%', padding: '18px', background: info.bg, color: 'white', border: 'none', borderRadius: '16px', fontWeight: '900', fontSize: '1rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: `0 6px 20px ${info.color}40` }}>
                {gs.startNow}
              </button>
            </div>
          )}

          {gameState === 'playing' && info && (
            <>
              {selectedGame === 'flashcard'   && <FlashCardGame   questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'vocabulary'  && <VocabInContextGame questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'reading'     && <ReadingGame     questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'listening'   && <ListeningGame   questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'complete'    && <CompleteGame    questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'wordbuilder' && <WordBuilderGame questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'oddout'      && <OddOutGame      questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'modismo'     && <ModismoGame     questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'verdfalso'   && <VerdFalsoGame   questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'conjugate'   && <ConjugateGame   questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'preposition' && <PrepositionGame questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'writing'     && <WritingGame     questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'speaking'    && <SpeakingGame    questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'dictation'   && <DictationGame   questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
              {selectedGame === 'translation' && <TranslationGame questions={questions} onComplete={handleGameComplete} gs={gs} previouslySeenIds={previouslySeenIds} poolTotal={poolTotal} />}
            </>
          )}

          {gameState === 'score' && selectedGame && (
            <>
              <ScoreScreen
                score={score} total={total} gameType={selectedGame} onSave={saveResult} saving={saving} saved={saved} gs={gs}
                onReplay={() => { setSaved(false); startOrReplay(); }}
                onMenu={() => { setGameState('menu'); setSaved(false); }}
              />
            </>
          )}
        </div>
      )}

      {tab === 'plan' && (
        <div className="practice-card">
          {!planLevel ? (
            <StudyPlanLevelPicker gs={gs} onPick={choosePlanLevel} />
          ) : (
            <StudyPlanView level={planLevel} gs={gs} session={session} onChangeLevel={() => setPlanLevel(null)} onLaunchTask={launchPlanTask} />
          )}
        </div>
      )}

      {tab === 'phrase' && (
        <div className="practice-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#dc2626', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '20px' }}>{gs.phraseTitle}</div>
          <div style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', borderRadius: '20px', padding: '32px 24px', marginBottom: '20px', boxShadow: '0 12px 36px rgba(220,38,38,0.25)' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: '900', color: 'white', lineHeight: 1.3, marginBottom: '16px' }}>"{todayPhrase.es}"</div>
            <div style={{ height: '1px', background: 'rgba(255,255,255,0.2)', marginBottom: '16px' }} />
            <div style={{ fontSize: '1rem', fontWeight: '700', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>{lang === 'en' ? todayPhrase.en : lang === 'pt' ? todayPhrase.pt : todayPhrase.pt}</div>
          </div>
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '18px 20px', textAlign: 'left' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>{gs.phraseExample}</div>
            <div style={{ fontSize: '0.92rem', fontWeight: '600', color: '#475569', fontStyle: 'italic', lineHeight: 1.6 }}>{todayPhrase.ex}</div>
          </div>
          <div style={{ marginTop: '20px', fontSize: '0.78rem', color: '#94a3b8', fontWeight: '600' }}>{gs.phraseSub}</div>
        </div>
      )}

      {tab === 'mission' && (
        <div className="practice-card" style={{ textAlign: 'center' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg,#dc2626,#b91c1c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.4rem', margin: '0 auto 20px', boxShadow: '0 12px 36px rgba(220,38,38,0.3)' }}>
            {currentMission.icon}
          </div>
          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#dc2626', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>{gs.missionTitle}</div>
          <div style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', lineHeight: 1.5, marginBottom: '28px' }}>{lang === 'en' ? currentMission.en : lang === 'pt' ? currentMission.pt : currentMission.es}</div>
          <div style={{ background: '#fff1f2', border: '1.5px solid #fecdd3', borderRadius: '14px', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#9f1239', lineHeight: 1.6 }}>🎯 Haz esto en algún momento de esta semana — en una reunión, correo, conversación o practicando solo. El objetivo es salir de la zona de confort en español.</div>
          </div>
          {missionDone ? (
            <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: '14px', padding: '20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ fontSize: '2rem' }}>🏅</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '900', color: '#15803d', fontSize: '1rem' }}>{gs.missionDone}</div>
                <div style={{ fontWeight: '600', color: '#16a34a', fontSize: '0.8rem' }}>{gs.missionNext}</div>
              </div>
            </div>
          ) : (
            <button onClick={saveMission} disabled={missionSaving} style={{ width: '100%', padding: '18px', background: missionSaving ? '#f1f5f9' : 'linear-gradient(135deg,#dc2626,#b91c1c)', color: missionSaving ? '#94a3b8' : 'white', border: 'none', borderRadius: '16px', fontWeight: '900', fontSize: '1rem', cursor: missionSaving ? 'default' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', boxShadow: missionSaving ? 'none' : '0 6px 20px rgba(220,38,38,0.3)' }}>
              {missionSaving ? gs.saving : gs.missionBtn}
            </button>
          )}

          {/* Completed missions history */}
          {allMissions.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>
                {gs.lang === 'en' ? `✅ ${allMissions.length} completed mission${allMissions.length !== 1 ? 's' : ''}` : gs.lang === 'pt' ? `✅ ${allMissions.length} missão(ões) concluída(s)` : `✅ ${allMissions.length} misión${allMissions.length !== 1 ? 'es' : ''} completada${allMissions.length !== 1 ? 's' : ''}`}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                {allMissions.map((m, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', background: '#f8fafc', borderRadius: '12px', padding: '12px 14px', border: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>✅</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.4 }}>{m.mission_text}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: '600', marginTop: '3px' }}>
                        {gs.lang === 'en' ? 'Week' : gs.lang === 'pt' ? 'Semana' : 'Semana'} {m.week_number} · {new Date(m.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'media' && (
        <div className="practice-card">
          <MediaTab gs={gs} />
        </div>
      )}

      {tab === 'grammar' && (
        <div className="practice-card">
          <GrammarSection session={session} gs={gs} />
        </div>
      )}
    </>
  );
}

const practiceStyles = `
  .practice-card { background: white; border-radius: 24px; padding: 28px 22px; box-shadow: 0 4px 20px rgba(0,0,0,0.07); margin-bottom: 20px; }
  @media (max-width: 480px) { .practice-card { padding: 22px 16px; } }
  @keyframes feedbackPop { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
`;
